import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  MagnifyingGlass, PaperPlaneRight, ChatCircleDots, Phone, ArrowsClockwise, Plus, ArrowLeft,
  Funnel, Lightning, ArrowsLeftRight, X, Tag, NotePencil, Info,
  Paperclip, Image as ImageIcon, VideoCamera, FileText, Microphone, MapPin, IdentificationCard, Stop,
  DownloadSimple, Question, ChatTeardropText, CaretLeft, QrCode, PhoneCall, CalendarBlank, Check, Clock, Star,
  Sparkle, Camera, UploadSimple, Globe,
} from "@phosphor-icons/react";
import { IntlAddLeadModal } from "@/components/InternationalPanel";
import { WaCallButton, WaBlockMenu } from "@/components/WaCallButton";
import { MediaPreviewModal, CameraModal, makeMediaItems, releaseMediaItems } from "@/components/ChatMediaComposer";
import { fmtIST, fmtISTTime, fmtSmartShort, fmtSmartLong, fmtTime12, fmtDaySeparator, istDayKey } from "@/lib/format";
import { StatusBadge, SourceBadge } from "@/components/Badges";
import OMSDataSection from "@/components/OMSDataSection";
import LeadReference from "@/components/LeadReference";
import CallAiSummary from "@/components/CallAiSummary";
import TagsEditor from "@/components/TagsEditor";
import { WaHeader, WaFooter, WaButtons } from "@/components/WaParts";
import { useCompany } from "@/context/CompanyContext";

const POLL_MS = 4000;
const STATUSES = ["new", "contacted", "qualified", "converted", "lost"];

const CALL_OUTCOMES = [
  { v: "connected", label: "Connected (Call Answered)" },
  { v: "busy", label: "Busy" },
  { v: "no_answer", label: "No Answer" },
  { v: "rejected", label: "Rejected (Call Declined)" },
  { v: "switched_off", label: "Switched Off / Out of Coverage" },
];

const OUTCOME_COLOR = {
  connected: "text-[#008A00]",
  busy: "text-[#E67E00]",
  no_answer: "text-[#B85F00]",
  rejected: "text-[#E60000]",
  switched_off: "text-gray-500",
};

// ---------------- Helpers ----------------
function tickFor(status) {
  if (!status) return "";
  if (status === "read") return "✓✓";
  if (status === "delivered") return "✓✓";
  if (status === "sent") return "✓";
  if (status === "received") return "";
  if (status === "failed") return "!";
  return "✓";
}
function tickColor(status) {
  if (status === "read") return "text-[#34B7F1]";
  if (status === "failed") return "text-[#E60000]";
  return "text-gray-400";
}
function previewText(m) {
  if (!m) return "—";
  const prefix = m.direction === "out" ? "" : "";
  return `${prefix}${(m.body || m.template_name || "(message)").slice(0, 80)}`;
}

function getEpoch(isoStr) {
  if (!isoStr) return 0;
  let clean = isoStr;
  if (typeof clean === "string") {
    clean = clean.replace(/\.(\d{3})\d+/, ".$1");
  }
  const d = new Date(clean);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

// ---------------- Page ----------------
// `international` = the International WhatsApp inbox (sidebar → International
// WhatsApp): only International team leads, including ones not on WhatsApp yet
// so the team can start the conversation. The main inbox leaves them out.
// /chat Source filter value -> API params
function chatSourceParams(v) {
  if (!v) return {};
  if (v.startsWith("type:")) return { lead_type: v.slice(5) };
  if (v.startsWith("src:")) return { source: v.slice(4) };
  return {};
}

export default function Chat({ international = false }) {
  const { user } = useAuth();
  const navTo = useNavigate();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(params.get("lead") || null);
  const [search, setSearch] = useState("");
  const [filterUnreplied, setFilterUnreplied] = useState(false);
  const [filterReplied, setFilterReplied] = useState(false);
  const [filterStarred, setFilterStarred] = useState(false);
  const [filterSource, setFilterSource] = useState("");   // "" | "src:<lead source>" | "type:im_buylead" | "type:im_enquiry"
  const [chatSources, setChatSources] = useState([]);
  useEffect(() => {
    if (international) return;
    api.get("/inbox/sources", { params: { intl: "exclude" } }).then(({ data }) => setChatSources(data || [])).catch(() => {});
  }, [international]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAssignee, setFilterAssignee] = useState(params.get("agent") || "");
  const [filterCountry, setFilterCountry] = useState("");
  const [intlCountries, setIntlCountries] = useState([]);
  const [execs, setExecs] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);

  const isAdmin = user.role === "admin";

  // Mobile detection — used to wire the phone back-button to "close current
  // chat" instead of navigating away from /chat entirely.
  const isMobilePage = useMemo(() => {
    if (typeof window === "undefined") return false;
    const touch = (("ontouchstart" in window) || (navigator.maxTouchPoints > 0));
    const narrow = window.matchMedia ? window.matchMedia("(max-width: 767px)").matches : window.innerWidth < 768;
    return touch && narrow;
  }, []);

  // On mobile, when the user opens a chat (activeId set) push a synthetic
  // history entry so the browser back-button pops the chat-thread view back
  // to the chat list instead of leaving /chat altogether.
  useEffect(() => {
    if (!isMobilePage) return;
    if (!activeId) return;
    window.history.pushState({ chatOpen: activeId }, "");
    const onPop = () => {
      // back button pressed while a chat is open on mobile → close chat
      setActiveId(null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [isMobilePage, activeId]);

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // Global cross-conversation message search (WhatsApp "Search by messages")
  const [searchMode, setSearchMode] = useState("chats"); // 'chats' | 'messages'
  const [msgResults, setMsgResults] = useState({ items: [], total: 0, loading: false });
  // Keep track of the active lead's conversation object (resolved from convs list)
  const activeConv = useMemo(() => convs.find((c) => c.id === activeId) || null, [convs, activeId]);

  // Jump-to-message states: when user clicks a search result, we set
  // pendingJumpMessageId, which is read by ChatThread once activeId resolves,
  // scroll to + flash that exact bubble after the conversation loads.
  const [pendingJumpMessageId, setPendingJumpMessageId] = useState(null);
  const listScrollRef = useRef(null);

  const handleToggleStar = async (leadId, currentStarred) => {
    try {
      setConvs((prev) =>
        prev.map((c) => (c.id === leadId ? { ...c, starred: !currentStarred } : c))
      );
      await api.patch(`/leads/${leadId}`, { starred: !currentStarred });
      toast.success(currentStarred ? "Lead unstarred" : "Lead starred");
    } catch (e) {
      toast.error(errMsg(e));
      setConvs((prev) =>
        prev.map((c) => (c.id === leadId ? { ...c, starred: currentStarred } : c))
      );
    }
  };

  const fetchConvs = useCallback(async (opts = {}) => {
    const { reset = false } = opts;
    const nextOffset = reset ? 0 : page * PAGE_SIZE;
    if (!reset && (loadingMore || !hasMore)) return;
    if (!reset) setLoadingMore(true);
    try {
      const { data } = await api.get("/inbox/conversations", {
        params: {
          q: search || undefined,
          only_unreplied: filterUnreplied || undefined,
          only_replied: filterReplied || undefined,
          status: filterStatus || undefined,
          assigned_to: filterAssignee || undefined,
          country: (international && filterCountry) || undefined,
          starred: filterStarred ? true : undefined,
          ...chatSourceParams(filterSource),
          intl: international ? "all" : "exclude",
          include_all: international ? true : undefined,
          limit: PAGE_SIZE,
          offset: nextOffset,
        },
      });
      const incoming = Array.isArray(data) ? data : [];
      // Merge & dedup on id; keep latest copy from this fetch (for live unread updates).
      setConvs((prev) => {
        const base = reset ? [] : prev;
        const map = new Map(base.map((c) => [c.id, c]));
        for (const c of incoming) map.set(c.id, c);
        const merged = Array.from(map.values());
        merged.sort((a, b) => {
          const ta = a.last_message?.at || a.last_in_at || a.last_out_at || a.last_action_at || "";
          const tb = b.last_message?.at || b.last_in_at || b.last_out_at || b.last_action_at || "";
          return getEpoch(tb) - getEpoch(ta);
        });
        return merged;
      });
      // hasMore inference: server returns up to PAGE_SIZE per call; a short page
      // means we've exhausted leads. Filter-driven drops can return fewer rows
      // even with more leads available — we keep paging until empty.
      setHasMore(incoming.length >= 1);
      setPage((p) => (reset ? 1 : p + 1));
    } catch (e) {
      const msg = errMsg(e, "");
      if (msg && !msg.toLowerCase().includes("network")) toast.error(msg);
    } finally {
      setLoadingMore(false);
    }
  }, [search, filterUnreplied, filterReplied, filterStatus, filterAssignee, filterCountry, filterStarred, filterSource, page, loadingMore, hasMore]);

  // Refresh-only fetch — re-pulls page 0 for live unread updates without
  // disrupting any further pages the user has scrolled into.
  const refreshFirstPage = useCallback(async () => {
    try {
      const { data } = await api.get("/inbox/conversations", {
        params: {
          q: search || undefined,
          only_unreplied: filterUnreplied || undefined,
          only_replied: filterReplied || undefined,
          status: filterStatus || undefined,
          assigned_to: filterAssignee || undefined,
          country: (international && filterCountry) || undefined,
          starred: filterStarred ? true : undefined,
          ...chatSourceParams(filterSource),
          intl: international ? "all" : "exclude",
          include_all: international ? true : undefined,
          limit: PAGE_SIZE,
          offset: 0,
        },
      });
      const incoming = Array.isArray(data) ? data : [];
      setConvs((prev) => {
        const map = new Map(prev.map((c) => [c.id, c]));
        for (const c of incoming) map.set(c.id, c);
        const merged = Array.from(map.values());
        merged.sort((a, b) => {
          const ta = a.last_message?.at || a.last_in_at || a.last_out_at || a.last_action_at || "";
          const tb = b.last_message?.at || b.last_in_at || b.last_out_at || b.last_action_at || "";
          return getEpoch(tb) - getEpoch(ta);
        });
        return merged;
      });
    } catch (_) { /* silent */ }
  }, [search, filterUnreplied, filterReplied, filterStatus, filterAssignee, filterCountry, filterStarred, filterSource]);

  // Run global message search (debounced 250ms via the effect re-trigger)
  const runMessageSearch = useCallback(async (term) => {
    const q = (term || "").trim();
    if (!q) { setMsgResults({ items: [], total: 0, loading: false }); return; }
    setMsgResults((r) => ({ ...r, loading: true }));
    try {
      const { data } = await api.get("/inbox/search-messages", { params: { q, limit: 50 } });
      setMsgResults({ items: data.items || [], total: data.total || 0, loading: false });
    } catch (e) {
      setMsgResults({ items: [], total: 0, loading: false });
      toast.error(errMsg(e));
    }
  }, []);

  // Infinite scroll handler — fires when the user scrolls within 200px of the
  // bottom of the chat-list panel. Throttled by `loadingMore` flag so we don't
  // double-fire on rapid scroll events. Declared AFTER fetchConvs to avoid
  // temporal-dead-zone (useCallback lowers to const).
  const onListScroll = useCallback(() => {
    const el = listScrollRef.current;
    if (!el) return;
    if (searchMode !== "chats") return;
    if (loadingMore || !hasMore) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 200) {
      fetchConvs();
    }
  }, [searchMode, loadingMore, hasMore, fetchConvs]);

  // Initial + filter-changes → reset to page 0 and refetch
  useEffect(() => {
    setHasMore(true);
    setPage(0);
    fetchConvs({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterUnreplied, filterReplied, filterStatus, filterAssignee, filterCountry, filterStarred, filterSource]);

  // Live polling — refresh ONLY page 0 (keeps further pages stable while user scrolls)
  useEffect(() => {
    const id = setInterval(refreshFirstPage, POLL_MS);
    return () => clearInterval(id);
  }, [refreshFirstPage]);

  // Escape key closes active chat thread
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (document.querySelector('[data-testid="lightbox"]')) return;
        setActiveId(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Debounced message search trigger — re-runs 250ms after the user stops typing
  // when in 'messages' search mode.
  useEffect(() => {
    if (searchMode !== "messages") return;
    const t = setTimeout(() => runMessageSearch(search), 250);
    return () => clearTimeout(t);
  }, [searchMode, search, runMessageSearch]);

  // Load execs for admin filter
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then(({ data }) => setExecs(data.filter(u => u.role === "executive" || u.role === "admin"))).catch(() => {});
  }, [isAdmin]);

  // Sync active lead id to URL — preserve tab/agent/phone deep-link params if present
  // so links from /qa or /leads remain shareable (copy-paste URL still restores state).
  useEffect(() => {
    const p = {};
    if (activeId) p.lead = activeId;
    const tab = params.get("tab");
    const phone = params.get("phone");
    if (tab) p.tab = tab;
    if (filterAssignee) p.agent = filterAssignee;
    if (phone) p.phone = phone;
    setParams(p, { replace: true });
  }, [activeId, filterAssignee, setParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep `filterAssignee` in sync with URL parameter changes
  useEffect(() => {
    const urlAgent = params.get("agent") || "";
    setFilterAssignee((cur) => (cur === urlAgent ? cur : urlAgent));
  }, [params.get("agent")]);

  // Keep `activeId` in sync when the URL `?lead=` param changes (e.g. user
  // navigates from Internal Q&A → /chat?lead=X while already on /chat — the
  // component doesn't remount, so we need to listen for the param change).
  useEffect(() => {
    const urlLead = params.get("lead") || null;
    setActiveId((cur) => (cur === urlLead ? cur : urlLead));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("lead")]);

  // When `activeId` is set but the lead isn't in our paginated `convs` page
  // (e.g. clicked a global message-search hit, or arrived via deep-link to a
  // lead that's beyond page 0), fetch it on-demand and merge into `convs`.
  // Without this, the chat panel would render the empty state.
  useEffect(() => {
    if (!activeId) return;
    if (convs.find((c) => c.id === activeId)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/inbox/conversations/${activeId}`);
        if (cancelled || !data || !data.id) return;
        setConvs((prev) => {
          if (prev.find((c) => c.id === data.id)) return prev;
          return [data, ...prev];
        });
      } catch (e) {
        // Silent on 404 / 403; user will just see the empty-state.
      }
    })();
    return () => { cancelled = true; };
  }, [activeId, convs]);

  // An International lead opened in the main inbox (deep link from /leads, a
  // follow-up, Q&A...) moves over to the International WhatsApp inbox.
  useEffect(() => {
    if (international || !activeConv?.international) return;
    navTo(`/international/chat?${params.toString()}`, { replace: true });
  }, [international, activeConv?.international]); // eslint-disable-line react-hooks/exhaustive-deps

  // Team list for the admin "assign to" choice in the International add-lead form
  const [intlMembers, setIntlMembers] = useState([]);
  useEffect(() => {
    if (!international) return;
    api.get("/international/team").then(({ data }) => { setIntlMembers(data?.members || []); setIntlCountries(data?.countries || []); }).catch(() => {});
  }, [international]);

  const totalUnread = convs.reduce((s, c) => s + (c.unread || 0), 0);
  // Capture the initial deep-link params ONCE so the URL-sync effect below doesn't
  // strip them before ChatThread mounts (convs are loaded async).
  const initialDeeplink = useMemo(() => ({
    tab: params.get("tab"),
    agent: params.get("agent"),
    phone: params.get("phone"),
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-full flex bg-[#EFEAE2]" data-testid="chat-page">
      <Lightbox />
      {/* LEFT SIDEBAR */}
      <aside
        className={`${activeId ? "hidden md:flex" : "flex"} w-full md:w-[380px] shrink-0 flex-col bg-white border-r border-gray-200`}
        data-testid="conv-sidebar"
      >
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold flex items-center gap-1">
              {international ? <><Globe size={11} /> International</> : "Inbox"}
            </div>
            <div className="font-chivo font-bold text-lg leading-none mt-0.5">
              {international ? "WhatsApp" : "Chats"} {totalUnread > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-[#25D366] text-white text-xs font-bold">{totalUnread}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={fetchConvs} className="p-2 hover:bg-gray-100" title="Refresh" data-testid="refresh-conv-btn">
              <ArrowsClockwise size={16} />
            </button>
            <button onClick={() => setShowNewChat(true)} className="bg-[#25D366] hover:bg-[#1da851] text-white p-2"
              title={international ? "Add international lead" : "Start new chat"} data-testid="new-chat-btn">
              <Plus size={16} weight="bold" />
            </button>
          </div>
        </div>
        <div className="px-3 py-2 border-b border-gray-200 space-y-2">
          <div className="relative">
            <MagnifyingGlass className="absolute left-2 top-2.5 text-gray-400" size={14} />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={searchMode === "chats" ? "Search chats by name or phone…" : "Search inside messages…"}
              className="w-full border border-gray-300 pl-7 pr-3 py-2 text-sm outline-none focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]"
              data-testid="conv-search-input" />
          </div>
          {/* WhatsApp-style mode toggle: Chats vs Messages */}
          <div className="flex border border-gray-300 text-[10px] uppercase tracking-widest font-bold" data-testid="conv-search-mode">
            <button
              onClick={() => setSearchMode("chats")}
              className={`flex-1 px-3 py-1.5 ${searchMode === "chats" ? "bg-gray-900 text-white" : "bg-white hover:bg-gray-50"}`}
              data-testid="search-mode-chats"
            >
              Chats
            </button>
            <button
              onClick={() => setSearchMode("messages")}
              className={`flex-1 px-3 py-1.5 ${searchMode === "messages" ? "bg-gray-900 text-white" : "bg-white hover:bg-gray-50"}`}
              data-testid="search-mode-messages"
            >
              Messages
            </button>
          </div>
          {/* Filters only relevant in chats mode */}
          {searchMode === "chats" && (
          <div className="flex items-center gap-2 flex-wrap">
            <FilterChip active={filterUnreplied} onClick={() => {
              setFilterUnreplied(v => !v);
              setFilterReplied(false);
            }} testId="filter-unreplied">Not replied</FilterChip>
            <FilterChip active={filterReplied} onClick={() => {
              setFilterReplied(v => !v);
              setFilterUnreplied(false);
            }} testId="filter-replied">Replied</FilterChip>
            <FilterChip active={filterStarred} onClick={() => setFilterStarred(v => !v)} testId="filter-starred">Starred</FilterChip>
            {!international && (
              <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}
                className={`border px-2 py-1 text-xs ${filterSource ? "border-gray-900 bg-gray-900 text-white" : "border-gray-300"}`} data-testid="filter-source">
                <option value="">All sources</option>
                <option value="src:IndiaMART">IndiaMART - all</option>
                <option value="type:im_enquiry">IndiaMART - enquiries</option>
                <option value="type:im_buylead">IndiaMART - buy leads</option>
                {chatSources.filter((s) => s.source !== "IndiaMART").map((s) => (
                  <option key={s.source} value={`src:${s.source}`}>{s.source} ({s.count})</option>
                ))}
              </select>
            )}
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-gray-300 px-2 py-1 text-xs" data-testid="filter-status">
              <option value="">All status</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {isAdmin && (
              <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)} className="border border-gray-300 px-2 py-1 text-xs" data-testid="filter-agent">
                <option value="">All agents</option>
                {execs.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            )}
            {international && (
              <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} className="border border-gray-300 px-2 py-1 text-xs" data-testid="filter-country">
                <option value="">All countries</option>
                {intlCountries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
          </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto" ref={(el) => { listScrollRef.current = el; }} onScroll={onListScroll} data-testid="conv-list-scroll">
          {searchMode === "messages" ? (
            // ---------- Global message-search results ----------
            (search.trim() === "") ? (
              <div className="p-8 text-center text-xs uppercase tracking-widest text-gray-400">
                <MagnifyingGlass size={48} className="mx-auto mb-3 text-gray-300" weight="light" />
                Type to search messages across all chats
              </div>
            ) : msgResults.loading ? (
              <div className="p-8 text-center text-xs uppercase tracking-widest text-gray-400" data-testid="msg-search-loading">Searching…</div>
            ) : msgResults.items.length === 0 ? (
              <div className="p-8 text-center text-xs uppercase tracking-widest text-gray-400" data-testid="msg-search-empty">No messages match "{search}"</div>
            ) : (
              <>
                <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-gray-500 font-bold border-b border-gray-100" data-testid="msg-search-count">
                  {msgResults.total} message{msgResults.total === 1 ? "" : "s"} match
                </div>
                {msgResults.items.map((hit) => (
                  <MessageHitRow
                    key={hit.message_id || `${hit.lead_id}-${hit.at}`}
                    hit={hit}
                    query={search.trim()}
                    onClick={() => {
                      // Navigate to the lead and ask the thread to scroll to this message id.
                      setPendingJumpMessageId(hit.message_id);
                      setActiveId(hit.lead_id);
                    }}
                  />
                ))}
              </>
            )
          ) : (
            // ---------- Chats list (paginated) ----------
            <>
              {convs.length === 0 ? (
                <div className="p-8 text-center text-xs uppercase tracking-widest text-gray-400">
                  <ChatCircleDots size={48} className="mx-auto mb-3 text-gray-300" weight="light" />
                  No conversations match these filters
                </div>
              ) : convs.map((c) => (
                <ConvRow key={c.id} c={c} active={c.id === activeId} onClick={() => setActiveId(c.id)} execs={execs} onToggleStar={handleToggleStar} />
              ))}
              {hasMore && convs.length > 0 && (
                <div className="p-3 text-center text-[10px] uppercase tracking-widest text-gray-400 font-bold" data-testid="conv-list-loading">
                  {loadingMore ? "Loading…" : "Scroll for more"}
                </div>
              )}
              {!hasMore && convs.length > 0 && (
                <div className="p-3 text-center text-[10px] uppercase tracking-widest text-gray-300 font-bold" data-testid="conv-list-end">
                  End of list
                </div>
              )}
            </>
          )}
        </div>
      </aside>

      {/* RIGHT THREAD */}
      <section className={`${activeId ? "flex" : "hidden md:flex"} flex-1 min-w-0 flex-col`}>
        {activeConv ? (
          <ChatThread
            conv={activeConv}
            user={user}
            execs={execs}
            onClose={() => setActiveId(null)}
            onChanged={refreshFirstPage}
            initialTab={initialDeeplink.tab}
            initialAgentId={initialDeeplink.agent}
            initialPhone={initialDeeplink.phone}
            jumpToMessageId={pendingJumpMessageId}
            onJumpConsumed={() => setPendingJumpMessageId(null)}
            onToggleStar={handleToggleStar}
          />
        ) : (
          <EmptyState />
        )}
      </section>

      {showNewChat && international && (
        <IntlAddLeadModal
          members={intlMembers}
          isAdmin={isAdmin}
          onClose={() => setShowNewChat(false)}
          onCreated={(id) => { setShowNewChat(false); fetchConvs({ reset: true }); if (id) setActiveId(id); }}
        />
      )}
      {showNewChat && !international && (
        <NewChatModal
          execs={execs}
          isAdmin={isAdmin}
          onClose={() => setShowNewChat(false)}
          onCreated={(lead) => { setShowNewChat(false); fetchConvs({ reset: true }); setActiveId(lead.id); }}
        />
      )}
    </div>
  );
}

// ---------------- Sidebar row ----------------
function ConvRow({ c, active, onClick, execs, onToggleStar }) {
  const last = c.last_message || {};
  const exec = execs.find(e => e.id === c.assigned_to);
  const hasUnread = (c.unread || 0) > 0;
  return (
    <div onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      role="button"
      tabIndex={0}
      data-testid={`conv-row-${c.id}`}
      className={`w-full text-left px-3 py-3 flex gap-3 border-b border-gray-100 transition-colors cursor-pointer ${
        active
          ? "bg-[#F0F2F5]"
          : hasUnread
            ? "bg-[#E7F7E6] hover:bg-[#D4F0D2] border-l-[3px] border-l-[#25D366]"
            : "hover:bg-gray-50 border-l-[3px] border-l-transparent"
      }`}
      data-unread={hasUnread || undefined}>
      <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center text-white font-bold text-sm shrink-0">
        {(c.customer_name || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1.5">
          <div className="flex items-center gap-1 min-w-0">
            <span className={`text-sm truncate ${hasUnread ? "font-bold text-gray-900" : "font-semibold"}`}>{c.customer_name || c.phone}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleStar(c.id, c.starred);
              }}
              className={`inline-flex items-center justify-center p-1 rounded-full hover:bg-gray-200 transition-colors flex-shrink-0 ${
                c.starred ? "text-[#E6B012]" : "text-gray-300 hover:text-gray-400"
              }`}
              title={c.starred ? "Unstar lead" : "Star lead"}
              data-testid={`lead-star-btn-chat-${c.id}`}
            >
              <Star size={14} weight={c.starred ? "fill" : "regular"} />
            </button>
          </div>
          <div className={`text-[10px] shrink-0 ml-2 ${hasUnread ? "text-[#25D366] font-bold" : "text-gray-500"}`} title={last.at ? fmtIST(last.at) : ""}>
            {last.at ? fmtSmartShort(last.at) : ""}
          </div>
        </div>
        {c.phone && (
          <div className="text-[11px] text-gray-500 font-mono flex items-center gap-1 mt-0.5">
            <Phone size={10} weight="bold" /> {c.phone}
          </div>
        )}
        <div className="flex items-center justify-between mt-0.5">
          <div className={`text-xs truncate flex-1 ${hasUnread ? "text-gray-900 font-semibold" : "text-gray-500"}`}>
            {last.direction === "out" && (
              <span className={`mr-1 ${tickColor(last.status)}`}>{tickFor(last.status)}</span>
            )}
            {previewText(last)}
          </div>
          <div className="flex items-center gap-1 shrink-0 ml-2">
            {hasUnread && (
              <span className="bg-[#25D366] text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="unread-badge">
                {c.unread}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <SourceBadge source={c.source} />
          <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold truncate">
            {exec ? exec.name : c.assigned_to ? "—" : "Unassigned"}
          </span>
          {c.unreplied && (
            <span className="text-[9px] uppercase tracking-widest text-[#E60000] font-bold">Reply pending</span>
          )}
          {c.internal_qa_status === "pending" && (
            <span className="inline-flex items-center gap-1 bg-[#FFF4E5] border border-[#E67E00] text-[#B85F00] text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5" data-testid={`qa-tag-pending-${c.id}`}>
              Question Asked
            </span>
          )}
          {c.internal_qa_status === "answered" && (
            <span className="inline-flex items-center gap-1 bg-[#E7F7E6] border border-[#008A00] text-[#005F00] text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5" data-testid={`qa-tag-answered-${c.id}`}>
              Answered
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${active ? "bg-gray-900 text-white border-gray-900" : "border-gray-300 text-gray-700 hover:bg-gray-100"}`}>
      {children}
    </button>
  );
}

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-[#EFEAE2]" data-testid="chat-empty-state">
      <ChatCircleDots size={64} weight="light" className="text-gray-400" />
      <h3 className="font-chivo font-bold text-xl mt-4">Pick a conversation</h3>
      <p className="text-sm text-gray-500 mt-2 max-w-sm">
        Select a chat on the left to view the conversation, send messages, or use a quick reply.
        New inbound WhatsApp messages appear here automatically.
      </p>
    </div>
  );
}

// ---------------- Global message-search result row ----------------
function MessageHitRow({ hit, query, onClick }) {
  // Highlight the matched substring(s) inside the snippet (case-insensitive).
  const renderSnippet = () => {
    const text = hit.snippet || hit.body || "";
    if (!query) return text;
    const lc = text.toLowerCase();
    const q = query.toLowerCase();
    const out = [];
    let i = 0;
    while (i < text.length) {
      const idx = lc.indexOf(q, i);
      if (idx === -1) { out.push(text.slice(i)); break; }
      if (idx > i) out.push(text.slice(i, idx));
      out.push(<mark key={idx} className="bg-[#FFF59D] px-0.5">{text.slice(idx, idx + q.length)}</mark>);
      i = idx + q.length;
    }
    return out;
  };
  const at = hit.at ? new Date(hit.at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "";
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-3 py-3 hover:bg-gray-50 border-b border-gray-100 transition-colors"
      data-testid={`msg-hit-${hit.message_id}`}
    >
      <div className="flex items-center justify-between mb-0.5">
        <div className="text-sm font-bold text-gray-900 truncate">{hit.lead_name || hit.lead_phone || "Lead"}</div>
        <div className="text-[9px] uppercase tracking-widest text-gray-400 font-bold shrink-0 ml-2">{at}</div>
      </div>
      <div className="text-xs text-gray-700 line-clamp-2">
        <span className="text-[9px] uppercase tracking-widest text-gray-500 font-bold mr-1">
          {hit.direction === "out" ? "You" : "Customer"}:
        </span>
        {renderSnippet()}
      </div>
    </button>
  );
}


// ---------------- Chat thread ----------------
function ChatThread({ conv, user, execs, onClose, onChanged, initialTab, initialAgentId, initialPhone, jumpToMessageId, onJumpConsumed, onToggleStar }) {
  // phone: details row folds away; the app bar / bottom menu hide while a chat is open
  const [showDetails, setShowDetails] = useState(false);
  const [showTranslator, setShowTranslator] = useState(false);
  useEffect(() => {
    document.body.dataset.chatThread = "1";
    return () => { delete document.body.dataset.chatThread; };
  }, []);
  const isAdmin = user.role === "admin";
  const { isFragvansh } = useCompany();
  const canMessage = isAdmin || conv.assigned_to === user.id;
  const [messages, setMessages] = useState([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showTpl, setShowTpl] = useState(false);
  const [showInfo, setShowInfo] = useState(initialTab === "internal");
  const [panelTab, setPanelTab] = useState(initialTab === "internal" ? "internal" : "details"); // 'details' | 'internal'
  const [internalQuote, setInternalQuote] = useState(null); // WA message selected to ask admin about
  const [internalPreselectAgent, setInternalPreselectAgent] = useState(initialAgentId || null);
  // In-chat search (#4) — searches body / caption / template_name within this thread
  const [showInChatSearch, setShowInChatSearch] = useState(false);
  const [inChatQuery, setInChatQuery] = useState("");
  const [searchHits, setSearchHits] = useState([]); // ids of matching messages
  const [searchCursor, setSearchCursor] = useState(0); // index into searchHits
  const [quickReplies, setQuickReplies] = useState([]);
  const [qrSearch, setQrSearch] = useState("");
  const [templates, setTemplates] = useState([]);
  const [savingMeta, setSavingMeta] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [replyTo, setReplyTo] = useState(null);  // {id, preview, direction}
  const [showAttach, setShowAttach] = useState(false);
  const [attachMode, setAttachMode] = useState(null);  // 'location' | 'contact' | null
  const [recording, setRecording] = useState(null);  // {recorder, chunks, startedAt} or null
  const [targetLang, setTargetLang] = useState("hi");
  const [translatingInput, setTranslatingInput] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [sendToAll, setSendToAll] = useState(false);
  // Unique numbers on this lead (primary + extras, deduped on last 10 digits),
  // primary first — drives the header number switcher and the send target.
  const allNumbers = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    [conv.phone, ...(conv.phones || [])].filter(Boolean).forEach((p) => {
      const d = String(p).replace(/\D/g, "").slice(-10);
      if (d && !seen.has(d)) { seen.add(d); out.push(p); }
    });
    return out;
  }, [conv.phone, conv.phones]);
  const uniquePhoneCount = allNumbers.length;
  // id → name for showing who sent each outbound WhatsApp message
  const userMap = React.useMemo(
    () => Object.fromEntries((execs || []).map((e) => [e.id, e.name])),
    [execs],
  );

  // Reads the recent human conversation and writes ONE suggested reply straight
  // into the composer. It never sends: the executive edits and hits send.
  const askAI = async () => {
    if (aiLoading) return;
    // Outside the 24h window only a template can be sent, so a free-text draft
    // would be unusable - don't spend an AI request on it.
    if (!within24h) { toast.error("24-hour window closed - send a template instead"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/suggest", { lead_id: conv.id });
      const draftText = (data.suggestion || (data.suggestions || [])[0] || "").trim();
      if (!draftText) { toast.error("AI could not draft a reply - try again"); return; }
      setDraft(draftText);
      inputRef.current?.focus();
      toast.success("Draft ready - edit it, then send");
    } catch (e) {
      toast.error(errMsg(e, "AI suggestion failed"));
    } finally {
      setAiLoading(false);
    }
  };

  const handleTranslateInput = async () => {
    if (!draft.trim()) return;
    setTranslatingInput(true);
    try {
      const { data } = await api.post("/translate", { text: draft, target_lang: targetLang });
      setDraft(data.translated_text);
      toast.success("Message translated!");
    } catch (err) {
      toast.error("Translation failed");
    } finally {
      setTranslatingInput(false);
    }
  };

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const fileKindRef = useRef("image");
  const inputRef = useRef(null);

  const [calls, setCalls] = useState([]);
  const [followups, setFollowups] = useState([]);

  // Call form
  const [callOutcome, setCallOutcome] = useState("");
  const [callPhone, setCallPhone] = useState("");
  const [callSummary, setCallSummary] = useState("");
  const [savingCall, setSavingCall] = useState(false);

  // Followup form
  const [fuDate, setFuDate] = useState("");
  const [fuNote, setFuNote] = useState("");
  const [savingFu, setSavingFu] = useState(false);

  const loadCallsAndFollowups = useCallback(async () => {
    if (!conv?.id) return;
    try {
      const [{ data: C }, { data: F }] = await Promise.all([
        api.get(`/leads/${conv.id}/calls`),
        api.get("/followups", { params: { lead_id: conv.id } }),
      ]);
      setCalls(C || []);
      setFollowups(F || []);
    } catch (e) {
      console.error("Failed to load calls/followups", e);
    }
  }, [conv?.id]);

  useEffect(() => {
    loadCallsAndFollowups();
    const allPhones = [conv.phone, ...(conv.phones || [])].filter(Boolean);
    if (conv.phone && (!callPhone || !allPhones.includes(callPhone))) {
      setCallPhone(conv.phone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id, conv.phone, conv.phones, loadCallsAndFollowups]);

  useEffect(() => {
    if (!conv?.id) return;
    const id = setInterval(loadCallsAndFollowups, POLL_MS);
    return () => clearInterval(id);
  }, [conv?.id, loadCallsAndFollowups]);

  const submitCall = async () => {
    if (!callOutcome) { toast.error("Pick a call outcome"); return; }
    if (!callPhone) { toast.error("Pick a phone number"); return; }
    setSavingCall(true);
    try {
      await api.post(`/leads/${conv.id}/calls`, {
        phone: callPhone,
        outcome: callOutcome,
        summary: callSummary.trim() || null,
      });
      setCallOutcome(""); setCallSummary("");
      toast.success("Call logged");
      loadCallsAndFollowups();
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingCall(false); }
  };

  const scheduleFollowup = async () => {
    if (!fuDate) { toast.error("Pick a date/time"); return; }
    setSavingFu(true);
    try {
      const dueIso = new Date(fuDate).toISOString();
      await api.post("/followups", { lead_id: conv.id, due_at: dueIso, note: fuNote });
      setFuDate(""); setFuNote("");
      toast.success("Follow-up scheduled");
      loadCallsAndFollowups();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingFu(false); }
  };

  const completeFollowup = async (fuId) => {
    try {
      await api.patch(`/followups/${fuId}`, { status: "done" });
      toast.success("Follow-up marked as completed");
      loadCallsAndFollowups();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Mobile detection — touch + small viewport. Re-evaluated on mount.
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    const touch = (("ontouchstart" in window) || (navigator.maxTouchPoints > 0));
    const narrow = window.matchMedia ? window.matchMedia("(max-width: 767px)").matches : window.innerWidth < 768;
    return touch && narrow;
  }, []);

  const exec = execs.find(e => e.id === conv.assigned_to);
  // Per-phone filter (#3 from /leads parity spec). When deep-linked from /leads
  // with `?phone=…`, this filters the WA history to only the conversations
  // addressed to/from that specific number — never merging across phones.
  // NOTE: /chat itself does NOT alter the lead's active_wa_phone — that's a
  // /leads-only concern. The filter here is purely a view-side restriction.
  const [phoneFilter, setPhoneFilter] = useState(initialPhone || "");
  // the number whose thread is on screen - replies for another number are dropped
  const phoneFilterRef = useRef(initialPhone || "");

  const winByPhone = conv.within_24h_by_phone || null;

  // The 24h window is derived from the messages ACTUALLY IN VIEW: the newest
  // inbound in this thread. With a number selected the thread holds only that
  // number's messages, so this is exactly that number's window — and it can
  // never disagree with what the agent is looking at.
  const within24h = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].direction === "in" && messages[i].at) {
        const t = new Date(messages[i].at).getTime();
        if (Number.isFinite(t)) return (Date.now() - t) < 24 * 3600 * 1000;
      }
    }
    if (messages.length === 0) {
      // thread not loaded yet — trust the server hint
      if (phoneFilter && winByPhone && Object.prototype.hasOwnProperty.call(winByPhone, phoneFilter)) {
        return !!winByPhone[phoneFilter];
      }
      return !!conv.within_24h;
    }
    return false;  // loaded, but this number never wrote in → template only
  }, [messages, phoneFilter, winByPhone, conv.within_24h]);

  const lastConvIdRef = useRef(conv.id);
  const messagesLengthRef = useRef(0);

  if (lastConvIdRef.current !== conv.id) {
    lastConvIdRef.current = conv.id;
    messagesLengthRef.current = 0;
  }

  // Per-number threads: when a lead has 2+ numbers each number is its OWN
  // conversation. On opening a lead we select the number that actually talked
  // most recently (active_wa_phone, else primary) instead of merging them.
  // Also resets when switching leads — a stale filter used to blank the thread.
  useEffect(() => {
    if (initialPhone) { setPhoneFilter(initialPhone); return; }
    const nums = [conv.phone, ...(conv.phones || [])].filter(Boolean);
    setPhoneFilter(nums.length > 1 ? (conv.active_wa_phone || conv.phone || "") : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id, initialPhone]);

  useEffect(() => {
    messagesLengthRef.current = messages.length;
  }, [messages.length]);

  const loadMessages = useCallback(async () => {
    try {
      const currentCount = messagesLengthRef.current > 0 ? messagesLengthRef.current : 20;
      const params = { limit: currentCount, offset: 0 };
      if (phoneFilter) params.phone = phoneFilter;
      const { data } = await api.get(`/leads/${conv.id}/messages`, { params });
      
      // Ignore if the lead - or the selected number - changed since the request was started
      if (lastConvIdRef.current !== conv.id) return;
      if (phoneFilterRef.current !== phoneFilter) return;

      setMessages((prev) => {
        if (!Array.isArray(data)) return prev;
        const cleanPrev = (prev && prev.length > 0 && prev[0].lead_id === conv.id) ? prev : [];
        if (cleanPrev.length === 0) return data;
        const sig = (m) => `${m.status || ""}|${m.body || ""}|${m.caption || ""}|${m.media_url || ""}|${(m.reactions || []).length}|${m.error || ""}`;
        
        const updatedIncoming = data.map((m) => {
          const old = cleanPrev.find(p => p.id === m.id);
          if (old && sig(old) === sig(m)) return old;
          return m;
        });

        const map = new Map(cleanPrev.map((m) => [m.id, m]));
        for (const m of updatedIncoming) {
          map.set(m.id, m);
        }
        const merged = Array.from(map.values());
        merged.sort((a, b) => getEpoch(a.at) - getEpoch(b.at));
        return merged;
      });
    } catch (e) {
      const msg = errMsg(e, "");
      if (msg && !msg.toLowerCase().includes("network")) toast.error(msg);
    }
  }, [conv.id, phoneFilter]);

  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    setMessages([]);
    setHasOlder(true);
    setLoadingOlder(false);
    isInitialLoadRef.current = true;
  }, [conv.id]);

  // Another number selected = another conversation: start its thread from scratch
  // (never merge it into the previous number's messages).
  useEffect(() => {
    if (phoneFilterRef.current === phoneFilter) return;
    phoneFilterRef.current = phoneFilter;
    messagesLengthRef.current = 0;
    setMessages([]);
    setHasOlder(true);
    setLoadingOlder(false);
    isInitialLoadRef.current = true;
  }, [phoneFilter]);

  useEffect(() => { loadMessages(); }, [loadMessages]);
  useEffect(() => {
    const id = setInterval(loadMessages, POLL_MS);
    return () => clearInterval(id);
  }, [loadMessages]);

  // Auto-mark-read when opening + after new inbound
  useEffect(() => {
    if (conv.unread > 0 && canMessage) {
      api.post(`/inbox/leads/${conv.id}/mark-read`).then(() => onChanged?.()).catch(() => {});
    }
    // eslint-disable-next-line
  }, [conv.id]);

  // Load quick replies + templates
  useEffect(() => {
    api.get("/quick-replies").then(({ data }) => setQuickReplies(data)).catch(() => {});
    // Only the manual reply templates are offered in chat — the order_* / cod /
    // abandoned_checkout templates are sent automatically by the system and must
    // NOT be triggerable by hand.
    const MANUAL_TEMPLATES = new Set(["reply_all", "reply_to_all", "fv_reply_all"]);
    api.get("/whatsapp/templates")
      .then(({ data }) => setTemplates(
        data.filter(t => MANUAL_TEMPLATES.has((t.name || "").toLowerCase())
          && (!t.status || t.status === "APPROVED" || !t.synced_from_meta))))
      .catch(() => {});
  }, []);

  // Refresh QR list when dropdown opens
  useEffect(() => {
    if (showQR) {
      api.get("/quick-replies").then(({ data }) => setQuickReplies(data)).catch(() => {});
    }
  }, [showQR]);

  // Auto-expand the message input as the user types (WhatsApp-style). Caps
  // at ~6 lines; beyond that the textarea internally scrolls.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = 144; // ~6 lines @ 24px line-height
    const next = Math.min(el.scrollHeight, max);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [draft]);

  // Scroll to bottom on new messages
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (isInitialLoadRef.current) {
      el.scrollTop = el.scrollHeight;
      if (messages.length > 0) {
        isInitialLoadRef.current = false;
      }
      return;
    }
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    if (isNearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length]);

  const onMessagesScroll = useCallback(async () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop < 50 && !loadingOlder && hasOlder) {
      setLoadingOlder(true);
      const prevScrollHeight = el.scrollHeight;
      const prevScrollTop = el.scrollTop;
      try {
        const currentLength = messages.length;
        const params = { limit: 10, offset: currentLength };
        if (phoneFilter) params.phone = phoneFilter;
        const { data } = await api.get(`/leads/${conv.id}/messages`, { params });
        
        // Ignore if the lead - or the selected number - changed since the request was started
        if (lastConvIdRef.current !== conv.id) return;
        if (phoneFilterRef.current !== phoneFilter) return;

        if (Array.isArray(data)) {
          if (data.length < 10) {
            setHasOlder(false);
          }
          if (data.length > 0) {
            const sig = (m) => `${m.status || ""}|${m.body || ""}|${m.caption || ""}|${m.media_url || ""}|${(m.reactions || []).length}|${m.error || ""}`;
            setMessages((prev) => {
              const cleanPrev = (prev && prev.length > 0 && prev[0].lead_id === conv.id) ? prev : [];
              const byId = new Map(cleanPrev.map((m) => [m.id, m]));
              const updatedIncoming = data.map((m) => {
                const old = byId.get(m.id);
                if (old && sig(old) === sig(m)) return old;
                return m;
              });

              const map = new Map(cleanPrev.map(m => [m.id, m]));
              for (const m of updatedIncoming) {
                map.set(m.id, m);
              }
              const merged = Array.from(map.values());
              merged.sort((a, b) => getEpoch(a.at) - getEpoch(b.at));
              return merged;
            });

            setTimeout(() => {
              if (scrollRef.current) {
                const newScrollHeight = scrollRef.current.scrollHeight;
                scrollRef.current.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
              }
            }, 50);
          }
        }
      } catch (e) {
        console.error("Failed to load older messages", e);
      } finally {
        setLoadingOlder(false);
      }
    }
  }, [conv.id, messages.length, phoneFilter, loadingOlder, hasOlder]);

  // WhatsApp-style jump-to-quoted-message. Scrolls the bubble into view + adds
  // a temporary highlight ring so the user knows where it landed.
  const jumpToMessage = useCallback((msgId) => {
    if (!msgId || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(`[data-testid="bubble-${msgId}"]`);
    if (!el) {
      toast.error("Original message not loaded");
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Drop a transient highlight class on the inner bubble div.
    const inner = el.querySelector('[data-testid="bubble-inner"]') || el.querySelector("div");
    if (inner) {
      inner.classList.add("ring-4", "ring-[#FF8800]", "ring-offset-1");
      setTimeout(() => {
        inner.classList.remove("ring-4", "ring-[#FF8800]", "ring-offset-1");
      }, 1400);
    }
  }, []);

  // Auto-jump when the parent passes `jumpToMessageId` (from a global message
  // search hit). We wait until messages are loaded, then attempt the jump.
  // The messages list may need a moment to render — retry briefly.
  useEffect(() => {
    if (!jumpToMessageId || !messages.length) return;
    let attempts = 0;
    const tryJump = () => {
      const el = scrollRef.current?.querySelector(`[data-testid="bubble-${jumpToMessageId}"]`);
      if (el) {
        jumpToMessage(jumpToMessageId);
        onJumpConsumed?.();
      } else if (attempts < 6) {
        attempts += 1;
        setTimeout(tryJump, 120);
      } else {
        toast.error("Message not in loaded window — scroll up to find it");
        onJumpConsumed?.();
      }
    };
    setTimeout(tryJump, 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpToMessageId, messages.length]);

  // Group messages by IST day key — used to render sticky day separators
  // (#2 WhatsApp-style). Memoized so we don't re-walk the array on each render.
  const messageGroups = useMemo(() => {
    // Calls appear in the timeline between the messages (internal only, never sent).
    // Only calls inside the loaded message window, so older calls don't pile up at
    // the top before older messages are scrolled in.
    const oldest = messages.length ? getEpoch(messages[0].at) : 0;
    const callItems = (calls || [])
      .filter((c) => c.outcome !== "initiated" && c.at && getEpoch(c.at) >= oldest - 60000)
      .map((c) => ({ id: `call-${c.id}`, at: c.at, __call: c }));
    const timeline = callItems.length
      ? [...messages, ...callItems].sort((a, b) => getEpoch(a.at) - getEpoch(b.at))
      : messages;
    const groups = [];
    let lastKey = "";
    for (const m of timeline) {
      const k = istDayKey(m.at);
      if (k !== lastKey) {
        groups.push({ dayKey: k, items: [] });
        lastKey = k;
      }
      groups[groups.length - 1].items.push(m);
    }
    return groups;
  }, [messages, calls]);

  // Keep the calls in the timeline fresh while the chat is open (summaries arrive
  // a minute or two after a call ends).
  useEffect(() => {
    const id = setInterval(() => { loadCallsAndFollowups(); }, 60000);
    return () => clearInterval(id);
  }, [loadCallsAndFollowups]);

  // Compute in-chat search hits (#4). Empty query → no hits.
  // Filtering is purely client-side over what's already rendered.
  useEffect(() => {
    const q = inChatQuery.trim().toLowerCase();
    if (!q) { setSearchHits([]); setSearchCursor(0); return; }
    const hits = [];
    for (const m of messages) {
      const haystack = [
        m.body, m.caption, m.template_name, m.media_filename,
        m.contact_name, m.location_address, m.location_name,
      ].filter(Boolean).join(" ").toLowerCase();
      if (haystack.includes(q)) hits.push(m.id);
    }
    setSearchHits(hits);
    setSearchCursor(hits.length > 0 ? 0 : 0);
  }, [inChatQuery, messages]);

  // Scroll the focused search hit into view as the cursor moves
  useEffect(() => {
    if (searchHits.length === 0 || !scrollRef.current) return;
    const id = searchHits[searchCursor];
    const el = scrollRef.current.querySelector(`[data-testid="bubble-${id}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [searchCursor, searchHits]);
  const focusedHitId = searchHits[searchCursor];

  const send = async () => {
    if (!draft.trim() || !canMessage) return;
    if (!within24h) {
      toast.error("Outside 24-hour window — please use a template");
      return;
    }
    setSending(true);
    try {
      const payload = { lead_id: conv.id, body: draft };
      if (replyTo?.id) payload.reply_to_message_id = replyTo.id;
      if (sendToAll) payload.to_all_numbers = true;
      else if (phoneFilter) payload.to_phone = phoneFilter;  // send to the selected number
      const { data } = await api.post("/whatsapp/send", payload);
      if (data?.multi) {
        toast[data.failed > 0 ? "warning" : "success"](
          `Sent to ${data.sent}/${data.sent + data.failed} numbers${data.failed > 0 ? " — some failed (number may be outside its 24h window)" : ""}`);
      }
      setDraft("");
      setReplyTo(null);
      loadMessages();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, "Failed to send"));
    } finally { setSending(false); }
  };

  const sendTemplate = async (tpl) => {
    // Backwards-compat: support being called with a string name OR a full template object
    const tplName = typeof tpl === "string" ? tpl : tpl?.name;
    const paramsRequired = typeof tpl === "string"
      ? (templates.find(t => t.name === tpl)?.params_required ?? 0)
      : Number(tpl?.params_required || 0);
    let templateParams = null;
    if (paramsRequired > 0) {
      // Prompt for each placeholder. {{1}} default-suggests customer name.
      templateParams = [];
      for (let i = 1; i <= paramsRequired; i++) {
        const suggestion = i === 1 ? (conv.customer_name || "") : "";
        const v = window.prompt(`Template "${tplName}" — value for {{${i}}}`, suggestion);
        if (v === null) return; // user cancelled
        templateParams.push(v);
      }
    }
    setSending(true);
    try {
      const payload = {
        lead_id: conv.id,
        body: `[Template: ${tplName}]`,
        template_name: tplName,
      };
      if (templateParams !== null) payload.template_params = templateParams;
      if (sendToAll) payload.to_all_numbers = true;
      else if (phoneFilter) payload.to_phone = phoneFilter;  // send to the selected number
      const { data } = await api.post("/whatsapp/send", payload);
      if (data?.multi) {
        toast[data.failed > 0 ? "warning" : "success"](`Template sent to ${data.sent}/${data.sent + data.failed} numbers`);
      } else {
        toast.success(`Template "${tplName}" sent`);
      }
      setShowTpl(false);
      loadMessages();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, "Template send failed"));
    } finally { setSending(false); }
  };

  const applyQR = async (qr) => {
    // Media QR → send immediately (image/video/document/audio + optional caption)
    if (qr.media_url && qr.media_type) {
      if (!within24h) {
        toast.error("Outside 24-hour window — cannot send media");
        return;
      }
      setSending(true);
      try {
        const cap = (qr.caption || qr.text || "").replace("{{name}}", conv.customer_name || "");
        const payload = {
          lead_id: conv.id,
          media_type: qr.media_type,
          media_url: qr.media_url,
        };
        if (cap.trim() && (qr.media_type === "image" || qr.media_type === "video" || qr.media_type === "document")) {
          payload.caption = cap.trim();
        }
        if (qr.media_type === "document" && qr.media_filename) payload.filename = qr.media_filename;
        if (replyTo?.id) payload.reply_to_message_id = replyTo.id;
        await api.post("/whatsapp/send-media", payload);
        setReplyTo(null);
        toast.success(`Sent ${qr.media_type}`);
        loadMessages();
        onChanged?.();
      } catch (e) {
        toast.error(errMsg(e, "Quick reply send failed"));
      } finally {
        setSending(false);
        setShowQR(false);
        setQrSearch("");
      }
      return;
    }
    // Text-only QR → append to draft for editing
    const text = (qr.text || "").replace("{{name}}", conv.customer_name || "");
    setDraft((d) => (d ? d + " " : "") + text);
    setShowQR(false);
    setQrSearch("");
  };

  const requestTransfer = async () => {
    const reason = window.prompt("Why do you need this lead transferred to you?", "Customer is on my pipeline.");
    if (reason === null) return;
    try {
      await api.post("/inbox/transfer-request", { lead_id: conv.id, reason });
      toast.success("Transfer request sent to admin");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const reassign = async (newAssigneeId) => {
    if (!isAdmin || !newAssigneeId || newAssigneeId === conv.assigned_to) return;
    setSavingMeta(true);
    try {
      await api.post(`/leads/${conv.id}/reassign`, { assigned_to: newAssigneeId });
      toast.success("Lead reassigned");
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingMeta(false); }
  };

  const setStatus = async (newStatus) => {
    if (!canMessage || newStatus === conv.status) return;
    setSavingMeta(true);
    try {
      await api.patch(`/leads/${conv.id}`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingMeta(false); }
  };

  const addNote = async () => {
    if (!newNote.trim() || !canMessage) return;
    setSavingMeta(true);
    try {
      await api.post(`/leads/${conv.id}/notes`, { body: newNote.trim() });
      setNewNote("");
      toast.success("Note added");
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingMeta(false); }
  };

  // ────────── Rich media send helpers ──────────
  const uploadAndSend = async (file, kind) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large (max 50 MB)");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data: up } = await api.post("/chatflows/upload-media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const payload = {
        lead_id: conv.id,
        media_type: kind,
        media_url: up.url,
      };
      if (kind === "document" && up.filename) payload.filename = up.filename;
      if (replyTo?.id) payload.reply_to_message_id = replyTo.id;
      await api.post("/whatsapp/send-media", payload);
      setReplyTo(null);
      loadMessages();
      onChanged?.();
      toast.success(`Sent ${kind}`);
    } catch (e) {
      toast.error(errMsg(e, `Failed to send ${kind}`));
    } finally {
      setSending(false);
      setShowAttach(false);
    }
  };

  // ────────── WhatsApp-style media: preview + caption before sending ──────────
  // Files arrive from the attach menu, a paste into the message box, a drop on
  // the chat, or the camera. They open in a preview where each one gets its own
  // caption; nothing is sent until Send is pressed in the preview.
  const [mediaItems, setMediaItems] = useState(null);
  const [mediaSending, setMediaSending] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const dragDepthRef = useRef(0);
  const movedDraftRef = useRef(null);   // composer text that became the first caption

  const closeMediaPreview = () => {
    releaseMediaItems(mediaItems);
    setMediaItems(null);
    movedDraftRef.current = null;
  };

  // A preview belongs to one conversation: never let it follow the user to another chat.
  useEffect(() => {
    setMediaItems((cur) => { releaseMediaItems(cur); return null; });
    setCameraOpen(false);
    movedDraftRef.current = null;
  }, [conv.id]);

  const openMediaPreview = async (files, { forceKind = null, takeDraft = false } = {}) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    if (!canMessage) { toast.error("You can't message this lead"); return; }
    if (!within24h) { toast.error("Outside the 24-hour window - only a template can be sent"); return; }
    const items = (await makeMediaItems(list.slice(0, 10), { forceKind })).filter((it) => {
      if (it.file.size > 50 * 1024 * 1024) { toast.error(`${it.file.name}: too large (max 50 MB)`); releaseMediaItems([it]); return false; }
      return true;
    });
    if (!items.length) return;
    if (list.length > 10) toast.message("Only the first 10 files were added");
    // Like WhatsApp: text already typed in the box becomes the first caption.
    if (takeDraft && !mediaItems && draft.trim() && items[0].kind !== "audio") {
      items[0].caption = draft.trim().slice(0, 1024);
      movedDraftRef.current = draft;
    }
    setMediaItems((cur) => (cur ? [...cur, ...items] : items));
  };

  const sendMediaItems = async () => {
    if (!mediaItems?.length || mediaSending) return;
    setMediaSending(true);
    const failed = [];
    let sent = 0;
    for (const it of mediaItems) {
      try {
        const fd = new FormData();
        fd.append("file", it.file);
        fd.append("kind", it.kind);
        const { data: up } = await api.post("/chatflows/upload-media", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const payload = { lead_id: conv.id, media_type: it.kind, media_url: up.url };
        if (it.kind === "document" && up.filename) payload.filename = up.filename;
        if (it.kind !== "audio" && it.caption.trim()) payload.caption = it.caption.trim();
        if (sent === 0 && replyTo?.id) payload.reply_to_message_id = replyTo.id;
        await api.post("/whatsapp/send-media", payload);
        sent += 1;
      } catch (e) {
        failed.push(it);
        toast.error(errMsg(e, `Failed to send ${it.file.name}`));
      }
    }
    setMediaSending(false);
    if (sent) {
      setReplyTo(null);
      loadMessages();
      onChanged?.();
    }
    if (failed.length) {
      // keep only what failed so it can be retried; release the rest
      releaseMediaItems(mediaItems.filter((i) => !failed.includes(i)));
      setMediaItems(failed);
      if (sent) toast.success(`Sent ${sent}, ${failed.length} failed - press Send to retry`);
      return;
    }
    if (movedDraftRef.current !== null && draft === movedDraftRef.current) setDraft("");
    toast.success(sent > 1 ? `Sent ${sent} files` : "Sent");
    closeMediaPreview();
  };

  const onDragEnterThread = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDropActive(true);
  };
  const onDragOverThread = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = canMessage && within24h ? "copy" : "none";
  };
  const onDragLeaveThread = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDropActive(false);
  };
  const onDropThread = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setDropActive(false);
    openMediaPreview(e.dataTransfer.files, { takeDraft: true });
  };

  const onComposerPaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return;            // plain text paste behaves as before
    e.preventDefault();
    openMediaPreview(files, { takeDraft: true });
  };

  const pickFile = (kind) => {
    fileKindRef.current = kind;
    if (fileInputRef.current) {
      fileInputRef.current.accept = kind === "image" ? "image/*" : kind === "video" ? "video/*" : kind === "audio" ? "audio/*" : "";
      fileInputRef.current.multiple = kind !== "audio";
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const onFilePicked = (e) => {
    const files = Array.from(e.target.files || []);
    setShowAttach(false);
    if (files.length) openMediaPreview(files, { forceKind: fileKindRef.current });
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Audio recording not supported in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/ogg;codecs=opus") ? "audio/ogg;codecs=opus" : "";
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunks.push(ev.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: mimeType || "audio/webm" });
        const ext = (blob.type.includes("ogg") ? "ogg" : "webm");
        const file = new File([blob], `voice_${Date.now()}.${ext}`, { type: blob.type });
        await uploadAndSend(file, "audio");
      };
      rec.start();
      setRecording({ rec, startedAt: Date.now() });
      setShowAttach(false);
    } catch (e) {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    if (recording?.rec) {
      try { recording.rec.stop(); } catch (e) {}
    }
    setRecording(null);
  };

  const sendLocation = async ({ latitude, longitude, name, address }) => {
    setSending(true);
    try {
      const payload = { lead_id: conv.id, latitude: Number(latitude), longitude: Number(longitude) };
      if (name) payload.name = name;
      if (address) payload.address = address;
      if (replyTo?.id) payload.reply_to_message_id = replyTo.id;
      await api.post("/whatsapp/send-location", payload);
      setReplyTo(null); setAttachMode(null); setShowAttach(false);
      loadMessages(); onChanged?.();
      toast.success("Location sent");
    } catch (e) { toast.error(errMsg(e, "Failed to send location")); }
    finally { setSending(false); }
  };

  const sendCallButton = async () => {
    setShowAttach(false);
    setSending(true);
    try {
      const payload = { lead_id: conv.id };
      if (replyTo?.id) payload.reply_to_message_id = replyTo.id;
      await api.post("/whatsapp/send-call-button", payload);
      setReplyTo(null);
      loadMessages(); onChanged?.();
      toast.success("Call button sent - when they tap it, the call rings in the LeadOrbit app");
    } catch (e) { toast.error(errMsg(e, "Failed to send call button")); }
    finally { setSending(false); }
  };

  const sendContact = async (contact) => {
    setSending(true);
    try {
      const payload = { lead_id: conv.id, ...contact };
      if (replyTo?.id) payload.reply_to_message_id = replyTo.id;
      await api.post("/whatsapp/send-contact", payload);
      setReplyTo(null); setAttachMode(null); setShowAttach(false);
      loadMessages(); onChanged?.();
      toast.success("Contact card sent");
    } catch (e) { toast.error(errMsg(e, "Failed to send contact")); }
    finally { setSending(false); }
  };

  const resendMessage = async (m) => {
    try {
      await api.post("/whatsapp/resend", { message_id: m.id });
      loadMessages();
      toast.success("Message resent");
    } catch (e) { toast.error(errMsg(e, "Resend failed")); }
  };

  const reactToMessage = async (m, emoji) => {
    try {
      await api.post("/whatsapp/react", { message_id: m.id, emoji });
      loadMessages();
    } catch (e) { toast.error(errMsg(e, "Reaction failed")); }
  };

  // Stable callback identities so React.memo'd Bubble doesn't re-render on every poll. (#5)
  const handleReply = useCallback((target) => setReplyTo({
    id: target.id,
    preview: (target.caption || target.body || "").slice(0, 120),
    direction: target.direction,
  }), []);
  const handleAskAdmin = useCallback((target) => {
    setInternalQuote({
      id: target.id,
      preview: (target.caption || target.body || "").slice(0, 120),
      direction: target.direction,
      at: target.at,
      msg_type: target.msg_type,
    });
    setShowInfo(true);
    setPanelTab("internal");
  }, []);
  const askAdminFn = canMessage && user.role === "executive" ? handleAskAdmin : null;
  const resendFn = canMessage ? resendMessage : null;
  const reactFn = canMessage ? reactToMessage : null;

  return (
    <>
    <div className="flex flex-col h-full overflow-hidden relative"
      onDragEnter={onDragEnterThread} onDragOver={onDragOverThread} onDragLeave={onDragLeaveThread} onDrop={onDropThread}>
      {dropActive && (
        <div className="absolute inset-0 z-40 bg-[#25D366]/10 border-4 border-dashed border-[#25D366] flex items-center justify-center pointer-events-none" data-testid="chat-drop-overlay">
          <div className="bg-white px-5 py-4 rounded-lg shadow-lg text-center">
            <UploadSimple size={32} className="mx-auto text-[#25D366]" />
            <div className="font-semibold mt-1">
              {!canMessage ? "You can't message this lead" : within24h ? "Drop files to send" : "24-hour window closed - templates only"}
            </div>
            {canMessage && within24h && <div className="text-xs text-gray-500">You can add a caption before sending</div>}
          </div>
        </div>
      )}
      {/* Top bar */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-2 py-2 md:px-4 md:py-3 flex items-center gap-2 md:gap-3" data-testid="chat-topbar">
        <button onClick={onClose} className="md:hidden p-1 -ml-1" data-testid="back-btn"><ArrowLeft size={18} /></button>
        <div className="w-8 h-8 md:w-9 md:h-9 shrink-0 rounded-full bg-[#25D366] flex items-center justify-center text-white font-bold text-sm">
          {(conv.customer_name || "?").slice(0,1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 md:gap-2 md:flex-wrap min-w-0">
            <button type="button" onClick={() => setShowDetails((v) => !v)} className="font-semibold truncate text-left min-w-0" data-testid="chat-name-toggle">
              {conv.customer_name}
            </button>
            <button
              onClick={() => onToggleStar(conv.id, conv.starred)}
              className={`inline-flex items-center justify-center p-1 rounded-full hover:bg-gray-100 transition-colors ${
                conv.starred ? "text-[#E6B012]" : "text-gray-300 hover:text-gray-400"
              }`}
              title={conv.starred ? "Unstar lead" : "Star lead"}
              data-testid={`lead-star-btn-thread-${conv.id}`}
            >
              <Star size={16} weight={conv.starred ? "fill" : "regular"} />
            </button>
            {/* Inline status dropdown — disabled for non-owners */}
            <select
              value={conv.status || "new"}
              onChange={(e) => setStatus(e.target.value)}
              disabled={!canMessage || savingMeta}
              data-testid="chat-status-select"
              className={`${showDetails ? "inline-block" : "hidden"} md:inline-block border border-gray-300 px-1.5 py-0.5 text-[10px] uppercase tracking-widest font-bold disabled:bg-gray-100 disabled:cursor-not-allowed`}
            >
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {!showDetails && (
            <button type="button" onClick={() => setShowDetails(true)} className="md:hidden w-full text-left text-[11px] text-gray-500 flex items-center gap-1.5 min-w-0" data-testid="chat-details-summary">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${within24h ? "bg-[#25D366]" : "bg-[#E60000]"}`} />
              <span className="font-mono truncate">{phoneFilter || conv.phone}</span>
              <span className="shrink-0">· {conv.status || "new"}</span>
              <span className="truncate">· {exec ? exec.name : "Unassigned"}</span>
              <span className="shrink-0 text-[#002FA7] font-bold">▾</span>
            </button>
          )}
          <div className={`${showDetails ? "flex" : "hidden"} md:flex text-xs text-gray-500 items-center gap-2 md:gap-3 flex-wrap mt-1 md:mt-0`}>
            {allNumbers.length <= 1 ? (
              conv.phone && (
                <a href={`tel:${conv.phone}`} className="font-mono flex items-center gap-1 hover:underline hover:text-blue-600" data-testid="chat-phone-display">
                  <Phone size={11} /> {conv.phone}
                </a>
              )
            ) : (
              <span className="flex items-center gap-1 flex-wrap" data-testid="chat-number-switcher">
                <Phone size={11} className="text-gray-400" />
                <button
                  onClick={() => setPhoneFilter("")}
                  className={`font-mono px-1.5 py-0.5 border text-[11px] ${!phoneFilter ? "bg-[#128C7E] border-[#128C7E] text-white" : "border-gray-300 hover:bg-gray-100"}`}
                  data-testid="chat-number-all"
                >All</button>
                {allNumbers.map((p, i) => {
                  const open = winByPhone ? !!winByPhone[p] : null;
                  return (
                    <button
                      key={p}
                      onClick={() => setPhoneFilter(p)}
                      className={`font-mono px-1.5 py-0.5 border text-[11px] flex items-center gap-1 ${phoneFilter === p ? "bg-[#128C7E] border-[#128C7E] text-white" : "border-gray-300 hover:bg-gray-100"}`}
                      title={`${i === 0 ? "Primary number" : "Additional number"}${open === null ? "" : open ? " · 24h window OPEN (can send free text)" : " · 24h window closed (template only)"}`}
                      data-testid={`chat-number-${i}`}
                    >
                      {open !== null && (
                        <span className={`w-1.5 h-1.5 rounded-full ${open ? "bg-[#25D366]" : "bg-[#E60000]"}`} />
                      )}
                      {p}{i === 0 ? " ★" : ""}
                    </button>
                  );
                })}
              </span>
            )}
            {conv.id && <WaCallButton leadId={conv.id} />}
            {conv.id && <WaBlockMenu conv={conv} onChanged={onChanged} />}
            {/* Admin: reassign dropdown ; executive: read-only assignee */}
            {isAdmin ? (
              <span className="flex items-center gap-1">
                <Tag size={11} />
                <select
                  value={conv.assigned_to || ""}
                  onChange={(e) => reassign(e.target.value)}
                  disabled={savingMeta}
                  data-testid="chat-reassign-select"
                  className="border border-gray-300 px-1.5 py-0.5 text-[11px] disabled:bg-gray-100"
                >
                  <option value="">— Unassigned —</option>
                  {execs.map(x => <option key={x.id} value={x.id}>{x.role === "admin" ? `${x.name} (admin)` : x.name}</option>)}
                </select>
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <Tag size={11} /> {exec ? exec.name : "Unassigned"}
              </span>
            )}
            {within24h ? (
              <span className="text-[#25D366] font-bold uppercase tracking-widest text-[9px]">24h window OPEN</span>
            ) : (
              <span className="text-[#E60000] font-bold uppercase tracking-widest text-[9px]">24h window CLOSED — template only</span>
            )}
          </div>
        </div>
        <button onClick={() => setShowInChatSearch(v => !v)} className={`p-1.5 md:p-2 shrink-0 ${showInChatSearch ? "bg-gray-900 text-white" : "hover:bg-gray-100"}`} title="Search in chat" data-testid="chat-search-toggle">
          <MagnifyingGlass size={16} weight={showInChatSearch ? "bold" : "regular"} />
        </button>
        <button onClick={() => setShowInfo(v => !v)} className={`p-1.5 md:p-2 shrink-0 ${showInfo ? "bg-gray-900 text-white" : "hover:bg-gray-100"}`} title="Lead info" data-testid="chat-info-toggle">
          <Info size={16} weight={showInfo ? "fill" : "regular"} />
        </button>
        {!canMessage && (
          <button onClick={requestTransfer} className="border border-[#002FA7] text-[#002FA7] px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold hover:bg-[#002FA7] hover:text-white flex items-center gap-1" data-testid="request-transfer-btn">
            <ArrowsLeftRight size={12} /> Request transfer
          </button>
        )}
      </div>

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Messages column */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Per-phone filter banner — when deep-linked from /leads with ?phone=… */}
          {phoneFilter && (
            <div className="bg-[#FFF4E5] border-b border-[#E67E00] px-3 py-1.5 flex items-center gap-2 text-xs" data-testid="chat-phone-filter-banner">
              <Phone size={12} className="text-[#B85F00]" />
              <span className="text-[#B85F00] font-bold uppercase tracking-widest text-[10px]">Showing only:</span>
              <span className="font-mono text-[#B85F00]">{phoneFilter}</span>
              <button
                onClick={() => setPhoneFilter("")}
                className="ml-auto text-[10px] uppercase tracking-widest text-[#B85F00] hover:underline font-bold"
                data-testid="chat-phone-filter-clear"
              >Show all numbers</button>
            </div>
          )}
          {/* In-chat search bar (#4) */}
          {showInChatSearch && (
            <div className="bg-[#F0F2F5] border-b border-gray-200 px-3 py-2 flex items-center gap-2" data-testid="in-chat-search-bar">
              <MagnifyingGlass size={14} className="text-gray-500" />
              <input
                value={inChatQuery}
                onChange={(e) => setInChatQuery(e.target.value)}
                placeholder="Search messages by name, phone or text…"
                autoFocus
                className="flex-1 bg-white border border-gray-300 px-2 py-1 text-sm outline-none focus:border-[#002FA7]"
                data-testid="in-chat-search-input"
              />
              {inChatQuery && (
                <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold" data-testid="in-chat-search-counter">
                  {searchHits.length === 0 ? "No matches" : `${searchCursor + 1} / ${searchHits.length}`}
                </span>
              )}
              <button
                onClick={() => setSearchCursor((c) => searchHits.length ? (c - 1 + searchHits.length) % searchHits.length : 0)}
                disabled={searchHits.length === 0}
                className="border border-gray-300 px-2 py-1 text-xs hover:bg-gray-100 disabled:opacity-40"
                title="Previous match"
                data-testid="in-chat-search-prev"
              >↑</button>
              <button
                onClick={() => setSearchCursor((c) => searchHits.length ? (c + 1) % searchHits.length : 0)}
                disabled={searchHits.length === 0}
                className="border border-gray-300 px-2 py-1 text-xs hover:bg-gray-100 disabled:opacity-40"
                title="Next match"
                data-testid="in-chat-search-next"
              >↓</button>
              <button
                onClick={() => { setShowInChatSearch(false); setInChatQuery(""); }}
                className="text-gray-500 hover:text-gray-900 px-1"
                title="Close"
                data-testid="in-chat-search-close"
              ><X size={14} /></button>
            </div>
          )}
          <div ref={scrollRef} onScroll={onMessagesScroll} className="flex-1 overflow-y-auto p-4 space-y-1.5" style={{ background: "#EFEAE2", contain: "content", overscrollBehavior: "contain", willChange: "scroll-position" }} data-testid="messages-area">
            {loadingOlder && (
              <div className="text-center py-2 text-xs text-gray-500 font-semibold" data-testid="older-messages-loading">
                Loading older messages...
              </div>
            )}
            {messages.length === 0 && (
              <div className="text-center text-xs uppercase tracking-widest text-gray-500 py-12">No messages yet</div>
            )}
            {messageGroups.map((g) => (
              <DayGroup
                key={g.dayKey}
                group={g}
                allMessages={messages}
                userMap={userMap}
                canMessage={canMessage && within24h}
                currentUserId={user?.id}
                searchQuery={inChatQuery.trim()}
                focusedHitId={focusedHitId}
                searchHitsSet={searchHits}
                onReply={handleReply}
                onResend={resendFn}
                onReact={reactFn}
                onAskAdmin={askAdminFn}
                onJumpTo={jumpToMessage}
                leadId={conv.id}
              />
            ))}
          </div>

          {/* Quick reply dropdown */}
          {showQR && (
            <div className="bg-white border-t border-gray-200 max-h-64 overflow-hidden flex flex-col" data-testid="qr-dropdown">
              <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                <input
                  autoFocus
                  type="text"
                  value={qrSearch}
                  onChange={(e) => setQrSearch(e.target.value)}
                  placeholder="Search quick replies…"
                  className="w-full border border-gray-300 px-2 py-1.5 text-xs outline-none focus:border-[#25D366]"
                  data-testid="qr-search-input"
                />
              </div>
              <div className="overflow-y-auto flex-1">
                {(() => {
                  const q = qrSearch.trim().toLowerCase();
                  const filtered = q
                    ? quickReplies.filter((qr) =>
                        (qr.title || "").toLowerCase().includes(q) ||
                        (qr.text || "").toLowerCase().includes(q) ||
                        (qr.caption || "").toLowerCase().includes(q) ||
                        (qr.media_filename || "").toLowerCase().includes(q)
                      )
                    : quickReplies;
                  if (filtered.length === 0) {
                    return (
                      <div className="px-4 py-6 text-xs uppercase tracking-widest text-gray-400 text-center" data-testid="qr-empty">
                        {quickReplies.length === 0 ? "No quick replies — create them in /quick-replies" : "No matches"}
                      </div>
                    );
                  }
                  return filtered.map((qr) => {
                    const previewText = (qr.text || qr.caption || (qr.media_filename ? `[${qr.media_type}] ${qr.media_filename}` : "")).replace(/\s+/g, " ").trim();
                    return (
                      <button
                        key={qr.id}
                        onClick={() => applyQR(qr)}
                        disabled={sending}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 disabled:opacity-50"
                        data-testid={`qr-${qr.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="text-xs font-bold uppercase tracking-widest text-gray-500 truncate flex-1">{qr.title}</div>
                          {qr.media_url && qr.media_type && (
                            <span className="bg-[#25D366] text-white px-1.5 py-0.5 text-[8px] uppercase tracking-widest font-bold shrink-0" data-testid={`qr-media-badge-${qr.id}`}>
                              {qr.media_type}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-800 truncate" data-testid={`qr-preview-${qr.id}`}>
                          {previewText || <span className="text-gray-400 italic">(no text)</span>}
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          )}
          {showTpl && (
            <div className="bg-white border-t border-gray-200 max-h-56 overflow-y-auto">
              {templates.length === 0 ? (
                <div className="px-4 py-6 text-xs uppercase tracking-widest text-gray-400 text-center">
                  No approved templates — sync them from Meta in /templates
                </div>
              ) : templates.map(t => (
                <button key={t.id} onClick={() => sendTemplate(t)} className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100" data-testid={`tpl-${t.name}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-bold">{t.name}</div>
                    <div className="flex items-center gap-1">
                      {Number(t.params_required || 0) > 0 ? (
                        <span className="kbd bg-[#002FA7] text-white border-[#002FA7]" data-testid={`tpl-params-${t.name}`}>{t.params_required} var{t.params_required === 1 ? "" : "s"}</span>
                      ) : (
                        <span className="kbd" data-testid={`tpl-params-${t.name}`}>no vars</span>
                      )}
                      <span className="kbd">{t.language || t.category}</span>
                    </div>
                  </div>
                  <WaHeader parts={t} className="text-xs mt-1" />
                  {t.body && <div className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{t.body}</div>}
                  <WaFooter parts={t} className="mt-1" />
                  <WaButtons parts={t} className="mt-1 max-w-xs" />
                </button>
              ))}
            </div>
          )}

          {/* Reply-to preview banner */}
          {replyTo && canMessage && (
            <div className="bg-white border-t border-gray-200 px-3 py-2 flex items-center gap-2" data-testid="reply-to-banner">
              <div className="flex-1 min-w-0 border-l-[3px] border-[#25D366] pl-2">
                <div className="text-[10px] uppercase tracking-widest text-[#25D366] font-bold">
                  Replying to {replyTo.direction === "out" ? "your message" : "customer"}
                </div>
                <div className="text-xs text-gray-700 truncate">{replyTo.preview || "(no preview)"}</div>
              </div>
              <button onClick={() => setReplyTo(null)} className="text-gray-500 hover:text-gray-900 p-1" data-testid="reply-to-cancel">
                <X size={14} />
              </button>
            </div>
          )}

          {/* Input */}
          {canMessage ? (
            <div className="bg-[#F0F2F5] border-t border-gray-200 p-2 md:p-3 flex flex-col gap-1.5 md:gap-2 relative" data-testid="chat-composer" style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>
              {/* Utility/Toolbar Row */}
              <div className="flex items-center justify-start gap-1.5 md:gap-2 flex-wrap">
                <div className="flex items-center gap-1 md:gap-1.5">
                  <input ref={fileInputRef} type="file" onChange={onFilePicked} className="hidden" data-testid="hidden-file-input" />
                  <button onClick={() => { setShowAttach(v => !v); setShowQR(false); setShowTpl(false); }} title="Attach"
                    className={`p-2 rounded hover:bg-gray-200 text-gray-700 ${showAttach ? "bg-gray-900 text-white" : ""}`} disabled={!within24h} data-testid="attach-toggle">
                    <Paperclip size={18} weight="bold" />
                  </button>
                  {showAttach && (
                    <div className="absolute bottom-full mb-2 left-3 bg-white border border-gray-200 shadow-md z-10 w-52 rounded-md py-1" data-testid="attach-menu">
                      <button onClick={() => pickFile("image")} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm" data-testid="attach-image"><ImageIcon size={16} className="text-[#C2410C]" /> Photo</button>
                      <button onClick={() => pickFile("video")} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm" data-testid="attach-video"><VideoCamera size={16} className="text-[#BE185D]" /> Video</button>
                      <button onClick={() => { setShowAttach(false); setCameraOpen(true); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm" data-testid="attach-camera"><Camera size={16} className="text-[#0F766E]" /> Camera</button>
                      <button onClick={() => pickFile("document")} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm" data-testid="attach-document"><FileText size={16} className="text-[#475569]" /> Document</button>
                      <button onClick={() => pickFile("audio")} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm" data-testid="attach-audio-file"><Microphone size={16} className="text-[#7C3AED]" /> Audio file</button>
                      <button onClick={startRecording} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm border-t border-gray-100" data-testid="attach-record"><Microphone size={16} weight="fill" className="text-[#E60000]" /> Record voice note</button>
                      <button onClick={() => { setAttachMode("location"); setShowAttach(false); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm border-t border-gray-100" data-testid="attach-location"><MapPin size={16} className="text-[#0891B2]" /> Location</button>
                      <button onClick={() => { setAttachMode("contact"); setShowAttach(false); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm" data-testid="attach-contact"><IdentificationCard size={16} className="text-[#15803D]" /> Contact</button>
                      <button onClick={() => { setAttachMode("payment_qr"); setShowAttach(false); }} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm border-t border-gray-100" data-testid="attach-payment-qr"><QrCode size={16} className="text-[#002FA7]" /> Payment QR</button>
                      <button onClick={sendCallButton} disabled={sending} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-sm border-t border-gray-100" data-testid="attach-call-button"><PhoneCall size={16} weight="fill" className="text-[#25D366]" /> Call button</button>
                    </div>
                  )}
                  <button onClick={() => { setShowQR(v => !v); setShowTpl(false); setShowAttach(false); }} title="Quick replies"
                    className={`p-2 rounded hover:bg-gray-200 text-gray-700 ${showQR ? "bg-gray-900 text-white" : ""}`} data-testid="qr-toggle">
                    <Lightning size={18} weight="bold" />
                  </button>
                  <button onClick={() => { setShowTpl(v => !v); setShowQR(false); setShowAttach(false); }} title="Templates"
                    className={`px-3 py-2 text-[10px] uppercase tracking-widest font-bold rounded ${showTpl ? "bg-gray-900 text-white" : "border border-gray-300 hover:bg-gray-200"}`} data-testid="tpl-toggle">
                    Tpl
                  </button>
                  <button onClick={askAI} disabled={aiLoading || !within24h}
                    title={within24h
                      ? "Draft a reply from the recent conversation (goes into the message box - nothing is sent)"
                      : "24-hour window closed - only a template can be sent, so AI drafting is unavailable"}
                    className={`px-2.5 py-2 text-[10px] uppercase tracking-widest font-bold rounded flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed ${aiLoading ? "bg-[#7C3AED] text-white" : "border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED]/10"}`}
                    data-testid="ai-toggle">
                    <Sparkle size={13} weight="fill" className={aiLoading ? "animate-pulse" : ""} />
                    {aiLoading ? "Drafting..." : "AI"}
                  </button>
                  <button type="button" onClick={() => setShowTranslator((v) => !v)} title="Translate"
                    className={`sm:hidden p-2 rounded ${showTranslator ? "bg-gray-900 text-white" : "hover:bg-gray-200 text-gray-700"}`} data-testid="translator-toggle">
                    <Globe size={18} weight="bold" />
                  </button>
                </div>

                <div className={`${showTranslator ? "flex" : "hidden"} sm:flex items-center gap-1 border border-gray-300 px-1.5 py-1 bg-white rounded shadow-sm shrink-0`} data-testid="composer-translator">
                  <select
                    value={targetLang}
                    onChange={(e) => setTargetLang(e.target.value)}
                    className="bg-transparent text-[11px] font-bold uppercase tracking-wider outline-none cursor-pointer border-0 py-0.5"
                    title="Target language for translation"
                    data-testid="composer-translate-lang-select"
                  >
                    <option value="hi">Hindi</option>
                    <option value="mr">Marathi</option>
                    <option value="gu">Gujarati</option>
                    <option value="ta">Tamil</option>
                    <option value="te">Telugu</option>
                    <option value="kn">Kannada</option>
                    <option value="ml">Malayalam</option>
                    <option value="bn">Bengali</option>
                    <option value="pa">Punjabi</option>
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="ar">Arabic</option>
                  </select>
                  <button
                    onClick={handleTranslateInput}
                    disabled={!draft.trim() || translatingInput}
                    className="text-[10px] hover:bg-gray-100 px-1.5 py-0.5 font-bold uppercase tracking-wider text-[#002FA7] disabled:opacity-40 rounded"
                    title="Translate composer text"
                    data-testid="composer-translate-btn"
                  >
                    {translatingInput ? "..." : "Translate"}
                  </button>
                </div>
                {uniquePhoneCount > 1 && !phoneFilter && (
                  <label className={`flex items-center gap-1.5 px-2 py-1.5 border rounded cursor-pointer select-none shrink-0 ${sendToAll ? "bg-[#25D366] border-[#25D366] text-white" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"}`}
                    title={`This lead has ${uniquePhoneCount} numbers — when on, every message/template goes to all of them`}
                    data-testid="send-all-numbers-toggle">
                    <input type="checkbox" checked={sendToAll} onChange={(e) => setSendToAll(e.target.checked)} className="hidden" />
                    <Phone size={13} weight="bold" />
                    <span className="text-[10px] uppercase tracking-widest font-bold">All {uniquePhoneCount} numbers</span>
                  </label>
                )}
              </div>

              {/* Message Input & Send Row */}
              <div className="flex items-end gap-2 w-full">
                {recording ? (
                  <div className="flex-1 flex items-center gap-2 bg-[#FFE9E9] border border-[#E60000] px-3 py-2 text-sm rounded-md" data-testid="recording-bar">
                    <span className="w-2 h-2 rounded-full bg-[#E60000] animate-pulse" />
                    <span className="text-[#E60000] font-bold">Recording…</span>
                    <button onClick={stopRecording} className="ml-auto bg-[#E60000] text-white px-3 py-1 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 rounded" data-testid="recording-stop">
                      <Stop size={12} weight="fill" /> Stop &amp; send
                    </button>
                  </div>
                ) : (
                  <textarea
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onPaste={onComposerPaste}
                    disabled={!within24h}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && !isMobile) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder={within24h ? "Type a message…" : "Outside 24-hour window — use a template (Tpl ↑)"}
                    rows={1}
                    style={{ maxHeight: 144 }}
                    className="flex-1 resize-none border border-gray-300 rounded px-3 py-2 text-sm leading-6 outline-none focus:border-[#25D366] disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    data-testid="chat-input"
                    data-mobile={isMobile ? "1" : "0"}
                  />
                )}
                {!recording && (
                  <button onClick={send} disabled={!draft.trim() || sending || !within24h}
                    className="bg-[#25D366] hover:bg-[#1da851] text-white p-2 rounded disabled:opacity-50 disabled:cursor-not-allowed" data-testid="chat-send-btn">
                    <PaperPlaneRight size={18} weight="fill" />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-[#FFE9E9] border-t border-[#E60000] p-3 text-center text-sm text-[#E60000]">
              You can't message this lead — assigned to <b>{exec?.name || "another agent"}</b>.
              <button onClick={requestTransfer} className="ml-2 underline font-bold" data-testid="composer-request-transfer">Request transfer</button>
            </div>
          )}
        </div>

        {/* Right info panel — slides over on mobile, side-by-side on lg+ */}
        {showInfo && (
          <aside
            className="fixed inset-y-0 right-0 z-30 w-full sm:w-[360px] lg:relative lg:inset-auto lg:w-[340px] lg:z-auto shrink-0 bg-white border-l border-gray-200 overflow-y-auto flex flex-col"
            data-testid="lead-info-panel"
          >
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Lead Details</div>
              <button onClick={() => setShowInfo(false)} className="text-gray-400 hover:text-gray-900"><X size={14} /></button>
            </div>
            {/* Tabs */}
            <div className="flex border-b border-gray-200 shrink-0" data-testid="lead-panel-tabs">
              <button
                onClick={() => setPanelTab("details")}
                className={`flex-1 px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center justify-center gap-1 ${panelTab === "details" ? "bg-[#002FA7] text-white" : "hover:bg-gray-100 text-gray-600"}`}
                data-testid="tab-details"
              >
                <Info size={12} /> Details
              </button>
              <button
                onClick={() => { setPanelTab("internal"); }}
                className={`flex-1 px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center justify-center gap-1 ${panelTab === "internal" ? "bg-[#002FA7] text-white" : "hover:bg-gray-100 text-gray-600"}`}
                data-testid="tab-internal"
              >
                <ChatTeardropText size={12} /> Internal Q&amp;A
              </button>
            </div>
            {panelTab === "details" ? (
            <div className="p-4 space-y-4 text-sm overflow-y-auto">
              <InfoRow label="Customer">{conv.customer_name || "—"}</InfoRow>
              <InfoRow label="Phone">
                {conv.phone ? (
                  <a href={`tel:${conv.phone}`} className="font-mono hover:underline hover:text-blue-600">
                    {conv.phone}
                  </a>
                ) : (
                  "—"
                )}
              </InfoRow>
              {conv.email && <InfoRow label="Email"><span className="font-mono text-xs break-all">{conv.email}</span></InfoRow>}
              <InfoRow label="Status"><StatusBadge status={conv.status} /></InfoRow>
              <InfoRow label="Starred">
                <button
                  onClick={() => onToggleStar(conv.id, conv.starred)}
                  className={`inline-flex items-center justify-center p-1 rounded-full hover:bg-gray-100 transition-colors ${
                    conv.starred ? "text-[#E6B012]" : "text-gray-300 hover:text-gray-400"
                  }`}
                  title={conv.starred ? "Unstar lead" : "Star lead"}
                  data-testid={`lead-star-btn-details-${conv.id}`}
                >
                  <Star size={16} weight={conv.starred ? "fill" : "regular"} />
                  <span className="text-xs font-semibold ml-1.5 text-gray-700">
                    {conv.starred ? "Starred" : "Not starred"}
                  </span>
                </button>
              </InfoRow>
              <LeadReference
                lead={conv}
                calls={calls}
                followups={followups}
                execs={execs}
                lastInAt={conv.last_in_at}
                lastOutAt={conv.last_out_at}
              />
              <InfoRow label="Requirement">
                <div className="text-xs text-gray-700 whitespace-pre-wrap">{conv.requirement || "—"}</div>
              </InfoRow>
              {isFragvansh && (
                <InfoRow label="Labels">
                  <TagsEditor
                    tags={conv.tags || []}
                    canEdit={canMessage}
                    onSave={(tags) => api.patch(`/leads/${conv.id}`, { tags })
                      .then(() => onChanged?.())
                      .catch((e) => toast.error(errMsg(e)))}
                  />
                </InfoRow>
              )}
              {(conv.city || conv.area || conv.state) && (
                <InfoRow label="Location">
                  <div className="text-xs text-gray-700">{[conv.area, conv.city, conv.state].filter(Boolean).join(", ")}</div>
                </InfoRow>
              )}
              <OMSDataSection leadId={conv.id} />

              {/* Call Activity Section */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">
                  <PhoneCall size={12} /> Call Activity ({calls.length})
                </div>
                {canMessage && (
                  <div className="border border-gray-200 bg-gray-50 p-2 space-y-2 mb-2 text-xs">
                    <div className="grid grid-cols-2 gap-1">
                      <select
                        value={callPhone}
                        onChange={(e) => setCallPhone(e.target.value)}
                        className="border border-gray-300 p-1 text-xs bg-white w-full outline-none"
                        data-testid="chat-call-phone-select"
                      >
                        {[conv.phone, ...(conv.phones || [])].filter(Boolean).map((p, i) => (
                          <option key={p} value={p}>{p}{i === 0 ? " (Primary)" : ""}</option>
                        ))}
                      </select>
                      <select
                        value={callOutcome}
                        onChange={(e) => setCallOutcome(e.target.value)}
                        className="border border-gray-300 p-1 text-xs bg-white w-full outline-none"
                        data-testid="chat-call-outcome-select"
                      >
                        <option value="">— Outcome —</option>
                        {CALL_OUTCOMES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                      </select>
                    </div>
                    {callOutcome === "connected" && (
                      <textarea
                        value={callSummary}
                        onChange={(e) => setCallSummary(e.target.value)}
                        placeholder="Note (optional) — the AI summary is added from the call recording"
                        rows={2}
                        className="w-full border border-gray-300 p-1.5 text-xs bg-white outline-none focus:border-[#002FA7]"
                        data-testid="chat-call-summary-input"
                      />
                    )}
                    <button
                      onClick={submitCall}
                      disabled={!callOutcome || savingCall}
                      className="w-full bg-[#002FA7] hover:bg-[#002288] text-white py-1.5 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50 flex items-center justify-center gap-1"
                      data-testid="chat-log-call-btn"
                    >
                      <PhoneCall size={12} weight="bold" /> Log Call
                    </button>
                  </div>
                )}
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {calls.length === 0 && <div className="text-xs text-gray-400 italic">No calls logged yet</div>}
                  {calls.map(c => {
                    const o = CALL_OUTCOMES.find(x => x.v === c.outcome);
                    return (
                      <div key={c.id} className="bg-gray-50 border border-gray-200 p-2 text-xs" data-testid={`chat-call-row-${c.id}`}>
                        <div className="flex items-center justify-between gap-1 flex-wrap">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[10px] uppercase tracking-widest font-bold ${OUTCOME_COLOR[c.outcome] || "text-gray-500"}`}>
                              {o?.label || c.outcome}
                            </span>
                            {c.direction && (
                              <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                                c.direction === "incoming" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"
                              }`}>
                                {c.direction}
                              </span>
                            )}
                          </div>
                          <span className="text-[10px] text-gray-500 font-mono">{fmtIST(c.at)}</span>
                        </div>
                        <div className="text-[10px] text-gray-400 font-mono mt-0.5">{c.phone} · logged by {c.by_user_name}</div>
                        {c.summary && <div className="text-gray-700 mt-1 whitespace-pre-wrap leading-normal">{c.summary}</div>}
                        <CallAiSummary ai={c.ai_summary} leadId={conv.id} />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Follow-ups Section */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">
                  <CalendarBlank size={12} /> Follow-ups ({followups.length})
                </div>
                {canMessage && (
                  <div className="border border-gray-200 bg-gray-50 p-2 space-y-2 mb-2 text-xs">
                    <input
                      type="datetime-local"
                      value={fuDate}
                      onChange={(e) => setFuDate(e.target.value)}
                      className="border border-gray-300 p-1 text-xs bg-white w-full outline-none"
                      data-testid="chat-followup-date-input"
                    />
                    <input
                      value={fuNote}
                      onChange={(e) => setFuNote(e.target.value)}
                      placeholder="Note (optional)…"
                      className="w-full border border-gray-300 p-1.5 text-xs bg-white outline-none focus:border-[#002FA7]"
                      data-testid="chat-followup-note-input"
                    />
                    <button
                      onClick={scheduleFollowup}
                      disabled={!fuDate || savingFu}
                      className="w-full bg-[#002FA7] hover:bg-[#002288] text-white py-1.5 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50 flex items-center justify-center gap-1"
                      data-testid="chat-schedule-followup-btn"
                    >
                      <CalendarBlank size={12} weight="bold" /> Schedule
                    </button>
                  </div>
                )}
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {followups.length === 0 && <div className="text-xs text-gray-400 italic">No follow-ups scheduled</div>}
                  {followups.map(f => {
                    const isOverdue = f.status === "pending" && new Date(f.due_at) < new Date();
                    return (
                      <div key={f.id} className="bg-gray-50 border border-gray-200 p-2 text-xs flex items-center justify-between gap-2" data-testid={`chat-followup-row-${f.id}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] uppercase tracking-widest font-bold ${f.status === "done" ? "text-[#008A00]" : f.status === "missed" ? "text-gray-500" : isOverdue ? "text-[#E60000]" : "text-[#002FA7]"}`}>
                              {f.status} {isOverdue && "(overdue)"}
                            </span>
                            <span className="text-[10px] text-gray-500 font-mono">{fmtIST(f.due_at)}</span>
                          </div>
                          {f.note && <div className="text-gray-700 mt-1 break-words">{f.note}</div>}
                        </div>
                        {f.status === "pending" && (
                          <button
                            onClick={() => completeFollowup(f.id)}
                            className="text-[10px] uppercase tracking-widest font-bold text-[#008A00] hover:underline flex items-center gap-0.5 shrink-0"
                            data-testid={`chat-complete-followup-btn-${f.id}`}
                          >
                            <Check size={12} /> Done
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Notes section */}
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2">
                  <NotePencil size={12} /> Notes ({(conv.notes || []).length})
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(conv.notes || []).length === 0 && (
                    <div className="text-xs text-gray-400 italic">No notes yet</div>
                  )}
                  {(conv.notes || []).map(n => (
                    <div key={n.id} className="bg-gray-50 border border-gray-200 p-2 text-xs" data-testid={`note-${n.id}`}>
                      <div className="text-gray-800 whitespace-pre-wrap">{n.body}</div>
                      <div className="text-[10px] text-gray-500 mt-1 font-mono">
                        {n.by_name || "—"} · {fmtIST(n.at)}
                      </div>
                    </div>
                  ))}
                </div>
                {canMessage && (
                  <div className="mt-3">
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note (visible to admin + assignee)…"
                      rows={2}
                      className="w-full border border-gray-300 px-2 py-1.5 text-xs outline-none focus:border-[#002FA7]"
                      data-testid="add-note-input"
                    />
                    <button
                      onClick={addNote}
                      disabled={!newNote.trim() || savingMeta}
                      className="mt-1.5 w-full bg-[#002FA7] hover:bg-[#002288] text-white px-2 py-1.5 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50"
                      data-testid="add-note-btn"
                    >
                      Add Note
                    </button>
                  </div>
                )}
              </div>
            </div>
            ) : (
            <InternalChat
              leadId={conv.id}
              currentUser={user}
              assignedTo={conv.assigned_to}
              execs={execs}
              quote={internalQuote}
              clearQuote={() => setInternalQuote(null)}
              preselectAgentId={internalPreselectAgent}
              onPreselectConsumed={() => setInternalPreselectAgent(null)}
              onJumpToWa={jumpToMessage}
            />
            )}
          </aside>
        )}
      </div>
    </div>
      {mediaItems && mediaItems.length > 0 && (
        <MediaPreviewModal
          items={mediaItems}
          setItems={setMediaItems}
          onClose={() => { if (!mediaSending) closeMediaPreview(); }}
          onSend={sendMediaItems}
          sending={mediaSending}
          customerName={conv.customer_name}
        />
      )}
      {cameraOpen && (
        <CameraModal
          onClose={() => setCameraOpen(false)}
          onCaptured={(files) => { setCameraOpen(false); openMediaPreview(files); }}
        />
      )}
      {attachMode === "location" && (
        <LocationSendModal onClose={() => setAttachMode(null)} onSend={sendLocation} sending={sending} />
      )}
      {attachMode === "contact" && (
        <ContactSendModal onClose={() => setAttachMode(null)} onSend={sendContact} sending={sending} lead={conv} />
      )}
      {attachMode === "payment_qr" && (
        <PaymentQRModal
          onClose={() => setAttachMode(null)}
          sending={sending}
          within24h={within24h}
          onSend={async ({ media_url, caption, filename }) => {
            try {
              setSending(true);
              await api.post("/whatsapp/send-media", {
                lead_id: conv.id,
                media_type: "image",
                media_url,
                caption,
                filename,
                ...(replyTo?.id ? { reply_to_message_id: replyTo.id } : {}),
              });
              setReplyTo(null);
              setAttachMode(null);
              toast.success("Payment QR sent");
              loadMessages();
              onChanged?.();
            } catch (e) {
              toast.error(errMsg(e, "Send failed"));
            } finally {
              setSending(false);
            }
          }}
        />
      )}
    </>
  );
}

function LocationSendModal({ onClose, onSend, sending }) {
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  // Office preset — CitSpray, Nagpur. Pre-filled per admin request.
  const OFFICE = {
    lat: "21.109974",
    lng: "79.064088",
    name: "CitSpray",
    address: "B wing, Poonam Heights, Pande Layout, Khamla, Nagpur - 440025",
  };
  const fillOffice = () => {
    setLat(OFFICE.lat);
    setLng(OFFICE.lng);
    setName(OFFICE.name);
    setAddress(OFFICE.address);
  };
  const sendOfficeNow = () => {
    // One-click variant — bypass the form so reps can fire-and-forget.
    onSend({ latitude: OFFICE.lat, longitude: OFFICE.lng, name: OFFICE.name, address: OFFICE.address });
  };
  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation unavailable"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setLat(p.coords.latitude.toFixed(6)); setLng(p.coords.longitude.toFixed(6)); },
      () => toast.error("Could not read your location"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };
  const submit = (e) => {
    e.preventDefault();
    if (!lat || !lng) { toast.error("Latitude and longitude are required"); return; }
    onSend({ latitude: lat, longitude: lng, name, address });
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md bg-white border border-gray-900 p-6 space-y-3" data-testid="send-location-modal">
        <h3 className="font-chivo font-black text-xl">Send Location</h3>
        {/* Office quick-action — one click sends immediately. */}
        <div className="border border-[#002FA7] bg-[#002FA7]/5 p-3" data-testid="loc-office-card">
          <div className="text-[10px] uppercase tracking-widest text-[#002FA7] font-bold mb-1">Office</div>
          <div className="text-sm font-bold">{OFFICE.name}</div>
          <div className="text-xs text-gray-600">{OFFICE.address}</div>
          <div className="text-[10px] font-mono text-gray-500 mt-0.5">{OFFICE.lat}, {OFFICE.lng}</div>
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={sendOfficeNow} disabled={sending} className="bg-[#25D366] hover:bg-[#1EB755] text-white px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50" data-testid="loc-send-office-btn">
              {sending ? "Sending…" : "Send office location"}
            </button>
            <button type="button" onClick={fillOffice} className="border border-gray-300 hover:border-gray-900 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold" data-testid="loc-fill-office-btn">
              Pre-fill
            </button>
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Or send a custom location</div>
        <div className="grid grid-cols-2 gap-2">
          <input required placeholder="Latitude" value={lat} onChange={(e) => setLat(e.target.value)} className="border border-gray-300 px-3 py-2 text-sm font-mono" data-testid="loc-lat" />
          <input required placeholder="Longitude" value={lng} onChange={(e) => setLng(e.target.value)} className="border border-gray-300 px-3 py-2 text-sm font-mono" data-testid="loc-lng" />
        </div>
        <input placeholder="Name (optional) — e.g. 'Office'" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="loc-name" />
        <input placeholder="Address (optional)" value={address} onChange={(e) => setAddress(e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="loc-address" />
        <button type="button" onClick={useMyLocation} className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline" data-testid="loc-use-mine">Use my current location</button>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="border border-gray-300 px-3 py-2 text-[10px] uppercase tracking-widest font-bold">Cancel</button>
          <button disabled={sending} className="bg-[#25D366] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50" data-testid="loc-send-btn">{sending ? "Sending…" : "Send"}</button>
        </div>
      </form>
    </div>
  );
}

function ContactSendModal({ onClose, onSend, sending, lead }) {
  const [name, setName] = useState(lead?.customer_name || "");
  const [phone, setPhone] = useState("");
  const [organization, setOrganization] = useState("");
  const [email, setEmail] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) { toast.error("Name and at least one phone required"); return; }
    const payload = { name: name.trim(), phones: [{ phone: phone.trim(), type: "CELL" }] };
    if (email.trim()) payload.emails = [{ email: email.trim(), type: "WORK" }];
    if (organization.trim()) payload.organization = organization.trim();
    onSend(payload);
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md bg-white border border-gray-900 p-6 space-y-3" data-testid="send-contact-modal">
        <h3 className="font-chivo font-black text-xl">Send Contact Card</h3>
        <input required placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="contact-name" />
        <input required placeholder="Phone with country code (+91…, +52…, +1…)" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm font-mono" data-testid="contact-phone" />
        <input placeholder="Email (optional)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="contact-email" />
        <input placeholder="Organization (optional)" value={organization} onChange={(e) => setOrganization(e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="contact-org" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="border border-gray-300 px-3 py-2 text-[10px] uppercase tracking-widest font-bold">Cancel</button>
          <button disabled={sending} className="bg-[#25D366] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50" data-testid="contact-send-btn">{sending ? "Sending…" : "Send"}</button>
        </div>
      </form>
    </div>
  );
}

// Generates and previews a UPI Payment QR, then sends it as a WA image with
// the bank details as caption. Spec: GST | Without GST tabs, dynamic amount,
// account picker (only shown when admin has added > 1 account in the bucket).
function PaymentQRModal({ onClose, onSend, sending, within24h }) {
  const [type, setType] = useState("gst");
  const [amount, setAmount] = useState("");
  const [accounts, setAccounts] = useState({ gst: [], no_gst: [] });
  const [accountId, setAccountId] = useState("");
  const [preview, setPreview] = useState(null); // {media_url, caption, ...}
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/settings/payment-qr").then(({ data }) => {
      if (cancelled) return;
      setAccounts(data || { gst: [], no_gst: [] });
    }).catch((e) => toast.error(errMsg(e, "Could not load payment accounts")));
    return () => { cancelled = true; };
  }, []);

  // Reset account picker when type changes; default to first account in the bucket.
  useEffect(() => {
    const list = (accounts[type] || []);
    setAccountId(list[0]?.id || "");
    setPreview(null);
  }, [type, accounts]);

  const currentAccount = (accounts[type] || []).find((a) => a.id === accountId);

  const generate = async () => {
    const n = parseInt(amount, 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Enter a positive whole-rupee amount"); return; }
    if (!accountId) { toast.error("Pick an account first"); return; }
    setGenerating(true);
    setPreview(null);
    try {
      const { data } = await api.post("/payment-qr/generate", { type, account_id: accountId, amount: n });
      setPreview(data);
    } catch (e) { toast.error(errMsg(e, "Could not generate QR")); }
    finally { setGenerating(false); }
  };

  const send = () => {
    if (!preview) { toast.error("Generate the QR first"); return; }
    if (!within24h) { toast.error("Outside 24-hour window — cannot send media"); return; }
    onSend({ media_url: preview.media_url, caption: preview.caption, filename: preview.filename });
  };

  const list = accounts[type] || [];
  const noAccounts = list.length === 0;

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); send(); }} className="w-full max-w-md bg-white border border-gray-900 p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="payment-qr-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-chivo font-black text-xl">Send Payment QR</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-900" data-testid="pqr-close">
            <X size={16} />
          </button>
        </div>

        {/* GST / Without-GST tabs */}
        <div className="flex border border-gray-300">
          {[
            { k: "gst", label: "GST" },
            { k: "no_gst", label: "Without GST" },
          ].map((t) => (
            <button key={t.k} type="button" onClick={() => setType(t.k)}
              className={`flex-1 px-3 py-2 text-[11px] uppercase tracking-widest font-bold ${type === t.k ? "bg-gray-900 text-white" : "bg-white hover:bg-gray-50"}`}
              data-testid={`pqr-tab-${t.k}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Account picker */}
        {noAccounts ? (
          <div className="border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs">
            No {type === "gst" ? "GST" : "Without GST"} accounts configured. Ask the admin to add one in <span className="kbd">/payment-settings</span>.
          </div>
        ) : (
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Account</div>
            <select value={accountId} onChange={(e) => { setAccountId(e.target.value); setPreview(null); }}
              className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="pqr-account-select">
              {list.map((a) => (
                <option key={a.id} value={a.id}>{a.label || a.name} · {a.upi_id}</option>
              ))}
            </select>
          </label>
        )}

        {currentAccount && (
          <div className="border border-gray-200 bg-gray-50 px-3 py-2 text-xs space-y-0.5" data-testid="pqr-account-card">
            <div><b>{currentAccount.name}</b></div>
            <div>{currentAccount.bank}{currentAccount.branch ? ` · ${currentAccount.branch}` : ""}</div>
            <div>A/c: <span className="font-mono">{currentAccount.account_number}</span> · IFSC: <span className="font-mono">{currentAccount.ifsc}</span></div>
            <div>UPI: <span className="font-mono">{currentAccount.upi_id}</span>{currentAccount.upi_phone ? ` · ${currentAccount.upi_phone}` : ""}</div>
          </div>
        )}

        {/* Amount */}
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Amount (₹) — whole rupees only</div>
          <input
            type="number" min={1} step={1} inputMode="numeric"
            value={amount}
            onChange={(e) => { setAmount(e.target.value.replace(/[^0-9]/g, "")); setPreview(null); }}
            placeholder="1500"
            className="w-full border border-gray-300 px-3 py-2 text-sm font-mono"
            data-testid="pqr-amount-input"
          />
        </label>

        {/* Generate button + preview */}
        <div className="flex items-center justify-between gap-2">
          <button type="button" onClick={generate} disabled={generating || noAccounts || !amount}
            className="flex-1 bg-[#002FA7] hover:bg-[#002288] text-white px-3 py-2 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50"
            data-testid="pqr-generate-btn">
            {generating ? "Generating…" : (preview ? "Regenerate" : "Generate QR")}
          </button>
        </div>

        {preview && (
          <div className="border border-gray-200 bg-white p-3 space-y-2" data-testid="pqr-preview">
            <img src={preview.media_url} alt="Payment QR" className="block mx-auto w-48 h-48 object-contain border border-gray-200" data-testid="pqr-preview-image" />
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Caption</div>
            <pre className="whitespace-pre-wrap text-xs font-mono bg-gray-50 border border-gray-200 p-2 max-h-40 overflow-y-auto" data-testid="pqr-preview-caption">{preview.caption}</pre>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="border border-gray-300 px-3 py-2 text-[10px] uppercase tracking-widest font-bold">Cancel</button>
          <button type="submit" disabled={sending || !preview || !within24h}
            className="bg-[#25D366] hover:bg-[#1EB755] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50"
            data-testid="pqr-send-btn">
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </form>
    </div>
  );
}


function InfoRow({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}

// A phone call shown inside the chat timeline (internal note for the team, never sent
// to the customer): who called, how long, and the AI summary of what was said.
function CallTimelineItem({ c, leadId }) {
  const [open, setOpen] = useState(false);
  const answered = (c.duration_seconds || 0) > 0;
  const incoming = c.direction === "incoming";
  const d = Math.round(c.duration_seconds || 0);
  const dur = d >= 60 ? `${Math.floor(d / 60)}m ${d % 60}s` : `${d}s`;
  const note = (c.summary || "").trim();
  const autoNote = !note || /Auto-logged via app|Synced via Android App|Call ended\./.test(note);
  if (!answered) {
    return (
      <div className="flex justify-center" data-testid={`timeline-call-${c.id}`}>
        <span className="text-[11px] text-gray-500 bg-white/70 border border-gray-200 rounded-full px-3 py-0.5">
          <PhoneCall size={11} className="inline mr-1 -mt-0.5" />
          {incoming ? "Missed incoming call" : "Call not answered"} · {fmtTime12(c.at)}{c.by_user_name ? ` · ${c.by_user_name}` : ""}
        </span>
      </div>
    );
  }
  return (
    <div className="flex justify-center" data-testid={`timeline-call-${c.id}`}>
      <div className="w-full max-w-[85%] bg-white border border-[#DDD6FE] rounded-lg shadow-sm px-3 py-2">
        <div className="flex items-center justify-between gap-2 text-[11px]">
          <span className="font-bold text-gray-800 flex items-center gap-1">
            <PhoneCall size={12} weight="fill" className="text-[#6D28D9]" />
            {incoming ? "Incoming call" : "Outgoing call"} · {dur}{c.by_user_name ? ` · ${c.by_user_name}` : ""}
            {c.pns_match && c.pns_match !== "none" && (
              <span className="ml-1 px-1 border border-[#002FA7] text-[#002FA7] text-[9px] uppercase tracking-widest" title="Matched from IndiaMART's call details">
                via IndiaMART{c.pns_match === "callback" ? " · probable" : ""}
              </span>
            )}
          </span>
          <span className="text-gray-400 font-mono">{fmtTime12(c.at)}</span>
        </div>
        {!autoNote && <div className="text-xs text-gray-700 mt-1 whitespace-pre-wrap"><b>Note:</b> {note}</div>}
        {c.ai_summary ? (
          <>
            <CallAiSummary ai={c.ai_summary} compact={!open} leadId={leadId} />
            <button onClick={() => setOpen((v) => !v)} className="mt-1 text-[10px] uppercase tracking-widest font-bold text-[#6D28D9] hover:underline">
              {open ? "Less" : "Details"}
            </button>
          </>
        ) : (
          <div className="text-[11px] text-gray-400 mt-1">No AI summary (no recording from this phone)</div>
        )}
      </div>
    </div>
  );
}

// DayGroup renders a sticky date separator + the bubbles for that calendar day.
// `position: sticky` + `top: 0` makes the separator pin to the top while scrolling
// through the day's messages — matching WhatsApp's behavior.
const DayGroup = React.memo(function DayGroup({
  group, allMessages, userMap, canMessage, currentUserId,
  onReply, onResend, onReact, onAskAdmin, onJumpTo,
  searchQuery, focusedHitId, searchHitsSet, leadId,
}) {
  const hitsSet = useMemo(
    () => (searchHitsSet && searchHitsSet.length ? new Set(searchHitsSet) : null),
    [searchHitsSet],
  );
  return (
    <div className="space-y-1.5" data-testid={`day-group-${group.dayKey}`}>
      <div
        className="sticky top-0 z-10 flex justify-center pointer-events-none py-1.5"
        data-testid={`day-separator-${group.dayKey}`}
      >
        <span
          className="bg-white/85 backdrop-blur-sm text-gray-700 text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-sm border border-gray-200"
          data-day-label={group.dayKey}
        >
          {fmtDaySeparator(group.dayKey)}
        </span>
      </div>
      {group.items.map((m) => m.__call ? (
        <CallTimelineItem key={m.id} c={m.__call} leadId={leadId} />
      ) : (
        <Bubble
          key={m.id}
          m={m}
          allMessages={allMessages}
          senderName={m.by_user_id ? (userMap?.[m.by_user_id] || null) : "Auto"}
          canMessage={canMessage}
          currentUserId={currentUserId}
          searchQuery={searchQuery}
          isHighlighted={hitsSet ? hitsSet.has(m.id) : false}
          isFocused={focusedHitId === m.id}
          onReply={onReply}
          onResend={onResend}
          onReact={onReact}
          onAskAdmin={onAskAdmin}
          onJumpTo={onJumpTo}
        />
      ))}
    </div>
  );
});


function BubbleImpl({ m, allMessages = [], senderName = null, onReply, onResend, onReact, onAskAdmin, onJumpTo, canMessage = true, currentUserId = null, isHighlighted = false, isFocused = false, searchQuery = "" }) {
  const isOut = m.direction === "out";
  const isSystem = m.direction === "system";
  const [pickerOpen, setPickerOpen] = useState(false);
  const [translatedText, setTranslatedText] = useState(null);
  const [translating, setTranslating] = useState(false);

  const handleTranslate = async () => {
    if (translatedText) {
      setTranslatedText(null);
      return;
    }
    const textToTranslate = m.body || m.caption || "";
    if (!textToTranslate.trim()) return;
    setTranslating(true);
    try {
      const { data } = await api.post("/translate", { text: textToTranslate, target_lang: "en" });
      setTranslatedText(data.translated_text);
    } catch (err) {
      toast.error("Translation failed");
    } finally {
      setTranslating(false);
    }
  };

  if (isSystem) {
    return (
      <div className="flex justify-center my-2">
        <div className="bg-white text-gray-600 text-xs px-3 py-1 shadow-sm">{m.body}</div>
      </div>
    );
  }
  const media = renderMedia(m);
  const hasStructured = media || m.msg_type === "location" || m.msg_type === "contacts";
  const captionText = m.caption || (hasStructured ? "" : m.body);
  const quoted = (m.reply_to_message_id
    ? allMessages.find((x) => x.id === m.reply_to_message_id)
    : null) || (m.reply_to_wamid
    ? allMessages.find((x) => x.wamid === m.reply_to_wamid)
    : null);
  const quotedPreview = quoted
    ? ((quoted.caption || quoted.body || "").slice(0, 120))
    : (m.reply_to_preview ? m.reply_to_preview.slice(0, 120) : null);
  const quotedDirection = quoted?.direction || (m.reply_to_wamid && isOut ? "in" : "out");
  // Aggregate reactions for display: {emoji: {count, mine}}
  const reactionAgg = {};
  (m.reactions || []).forEach((r) => {
    const k = r.emoji || "?";
    if (!reactionAgg[k]) reactionAgg[k] = { count: 0, mine: false, directions: new Set() };
    reactionAgg[k].count += 1;
    if (r.direction === "out" && currentUserId && r.user_id === currentUserId) reactionAgg[k].mine = true;
    reactionAgg[k].directions.add(r.direction);
  });
  const handleReact = (emoji) => {
    setPickerOpen(false);
    onReact?.(m, emoji);
  };
  return (
    <div className={`group flex ${isOut ? "justify-end" : "justify-start"} relative ${isHighlighted ? "transition-all" : ""}`} data-testid={`bubble-${m.id}`}>
      {/* keep legacy testid for back-compat */}
      <span className="hidden" data-testid={`msg-${m.id}`} aria-hidden="true" />
      {!isOut && canMessage && onReply && (
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-0.5 self-center mr-1">
          {onReact && (
            <button onClick={() => setPickerOpen(v => !v)} className="text-gray-500 hover:text-[#FF8800] text-sm" title="React" data-testid={`react-btn-${m.id}`}>
              😊
            </button>
          )}
          <button onClick={() => onReply(m)} className="text-gray-500 hover:text-[#25D366] text-[10px] uppercase tracking-widest font-bold px-1" title="Reply" data-testid={`reply-btn-${m.id}`}>
            ↩
          </button>
          <button onClick={handleTranslate} className="text-gray-500 hover:text-[#002FA7] text-sm px-1" title={translatedText ? "Show Original" : "Translate to English"} data-testid={`translate-btn-${m.id}`}>
            🌐
          </button>
          {onAskAdmin && (
            <button onClick={() => onAskAdmin(m)} className="text-gray-500 hover:text-[#002FA7] text-[10px] uppercase tracking-widest font-bold px-1" title="Ask admin about this message" data-testid={`ask-admin-btn-${m.id}`}>
              <Question size={13} weight="bold" />
            </button>
          )}
        </div>
      )}
      <div data-testid="bubble-inner" className={`max-w-[75%] ${media ? "p-1.5" : "px-3 py-2"} ${isOut ? "bg-[#D9FDD3]" : (m.msg_type === "unsupported" || m.msg_type === "errors") ? "bg-[#FFF8E1] border border-[#F0C36D] text-[#7A5200]" : "bg-white"} text-sm shadow-sm relative ${isHighlighted ? "ring-2 ring-[#FFCC00]" : ""} ${isFocused ? "ring-4 ring-[#FF8800] ring-offset-1" : ""}`}>
        {m.template_name && (
          <div className="text-[9px] uppercase tracking-widest text-gray-500 font-bold mb-1 px-2 pt-1">Template · {m.template_name}</div>
        )}
        {m.wa_parts && <WaHeader parts={m.wa_parts} hasMedia={!!media} className={`mb-1 ${media ? "px-2" : ""}`} />}
        {quotedPreview && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!onJumpTo) return;
              const targetId = quoted?.id || m.reply_to_message_id;
              if (targetId) {
                onJumpTo(targetId);
              } else if (m.reply_to_wamid) {
                const foundByWamid = allMessages.find((x) => x.wamid === m.reply_to_wamid);
                if (foundByWamid) onJumpTo(foundByWamid.id);
              }
            }}
            className={`block w-full text-left mb-1.5 border-l-[3px] pl-2 py-1 text-xs bg-black/5 hover:bg-black/10 active:bg-black/15 transition-colors cursor-pointer ${media ? "mx-1.5 mt-1.5" : ""}`}
            style={{ borderColor: quotedDirection === "out" ? "#25D366" : "#002FA7" }}
            data-testid={`quoted-preview-${m.id}`}
            title="Jump to original"
          >
            <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: quotedDirection === "out" ? "#128C7E" : "#002FA7" }}>
              {quotedDirection === "out" ? "You" : "Customer"}
            </div>
            <div className="text-gray-700 truncate">{quotedPreview}</div>
          </button>
        )}
        {media}
        {m.msg_type === "location" && renderLocation(m)}
        {m.msg_type === "contacts" && renderContacts(m)}
        {captionText && (
          <div className={`whitespace-pre-wrap break-words ${media ? "px-2 pt-1.5" : ""}`}>{captionText}</div>
        )}
        {m.wa_parts && <WaFooter parts={m.wa_parts} className={`mt-1 ${media ? "px-2" : ""}`} />}
        {translatedText && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-200 text-xs text-gray-700 italic" data-testid={`translation-${m.id}`}>
            <span className="font-bold not-italic text-[10px] uppercase tracking-widest text-[#002FA7] mr-1 block">Translated to English:</span>
            {translatedText}
          </div>
        )}
        {translating && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-200 text-[10px] uppercase tracking-widest text-gray-400 font-bold animate-pulse" data-testid={`translating-${m.id}`}>Translating...</div>
        )}
        <div className={`flex items-center justify-end gap-1 mt-1 ${media ? "px-2 pb-1" : ""}`}>
          {isOut && m.status === "failed" && onResend && (
            <button onClick={() => onResend(m)} className="text-[9px] text-[#E60000] uppercase tracking-widest font-bold hover:underline mr-2" data-testid={`resend-btn-${m.id}`}>
              ↻ Resend
            </button>
          )}
          {isOut && senderName && (
            <span className="text-[9px] text-gray-400 uppercase tracking-wider font-bold mr-1" title="Sent by" data-testid={`msg-sender-${m.id}`}>~ {senderName}</span>
          )}
          <span className="text-[10px] text-gray-500 font-mono" title={fmtSmartLong(m.at)}>{fmtTime12(m.at)}</span>
          {isOut && <span className={`text-[10px] ${tickColor(m.status)}`}>{tickFor(m.status)}</span>}
          {isOut && m.error && <span className="text-[9px] text-[#E60000] uppercase tracking-widest font-bold">{String(m.error).slice(0, 24)}</span>}
        </div>
        {m.wa_parts && <WaButtons parts={m.wa_parts} className={`mt-1 ${media ? "" : "-mx-3 -mb-2"}`} />}
        {/* Reaction badges anchored to bottom-start of the bubble */}
        {Object.keys(reactionAgg).length > 0 && (
          <div className={`absolute -bottom-3 ${isOut ? "left-2" : "right-2"} flex items-center gap-1`} data-testid={`reactions-${m.id}`}>
            {Object.entries(reactionAgg).map(([emoji, info]) => (
              <button
                key={emoji}
                onClick={() => onReact && info.mine ? onReact(m, "") : onReact && onReact(m, emoji)}
                className={`bg-white border ${info.mine ? "border-[#25D366]" : "border-gray-200"} rounded-full px-1.5 py-0.5 text-[11px] flex items-center gap-0.5 shadow-sm hover:scale-110 transition-transform`}
                title={info.mine ? "Click to remove" : `React ${emoji}`}
                data-testid={`reaction-badge-${emoji}-${m.id}`}
              >
                <span>{emoji}</span>
                {info.count > 1 && <span className="text-[10px] text-gray-600 font-bold">{info.count}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {isOut && canMessage && (onReply || onReact) && (
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-0.5 self-center ml-1">
          {onReact && (
            <button onClick={() => setPickerOpen(v => !v)} className="text-gray-500 hover:text-[#FF8800] text-sm" title="React" data-testid={`react-btn-${m.id}`}>
              😊
            </button>
          )}
          {onReply && (
            <button onClick={() => onReply(m)} className="text-gray-500 hover:text-[#25D366] text-[10px] uppercase tracking-widest font-bold px-1" title="Reply" data-testid={`reply-btn-${m.id}`}>
              ↩
            </button>
          )}
          {onAskAdmin && (
            <button onClick={() => onAskAdmin(m)} className="text-gray-500 hover:text-[#002FA7] text-[10px] uppercase tracking-widest font-bold px-1" title="Ask admin about this message" data-testid={`ask-admin-btn-${m.id}`}>
              <Question size={13} weight="bold" />
            </button>
          )}
        </div>
      )}
      {pickerOpen && (
        <div className={`absolute z-20 ${isOut ? "right-12" : "left-12"} -top-2 bg-white border border-gray-200 shadow-lg px-1.5 py-1.5 flex items-center gap-1`} data-testid={`emoji-picker-${m.id}`}
          onClick={(e) => e.stopPropagation()}>
          {["👍", "❤️", "😂", "😮", "😢", "🙏"].map((e) => (
            <button key={e} onClick={() => handleReact(e)} className="hover:bg-gray-100 w-7 h-7 flex items-center justify-center text-base" data-testid={`emoji-${e}-${m.id}`}>
              {e}
            </button>
          ))}
          <button onClick={() => setPickerOpen(false)} className="ml-1 text-gray-400 hover:text-gray-900 p-1"><X size={12} /></button>
        </div>
      )}
    </div>
  );
}
// Memoize Bubble — re-render only when its specific message reference, search-state,
// or callback set changes. Cuts ~80% of bubble re-renders during chat polling on
// large histories (#5 perf).
const Bubble = React.memo(BubbleImpl, (prev, next) => {
  if (prev.m !== next.m) return false;
  if (prev.senderName !== next.senderName) return false;
  if (prev.isHighlighted !== next.isHighlighted) return false;
  if (prev.isFocused !== next.isFocused) return false;
  if (prev.canMessage !== next.canMessage) return false;
  if (prev.currentUserId !== next.currentUserId) return false;
  if (prev.searchQuery !== next.searchQuery) return false;
  if (prev.onReply !== next.onReply) return false;
  if (prev.onResend !== next.onResend) return false;
  if (prev.onReact !== next.onReact) return false;
  if (prev.onAskAdmin !== next.onAskAdmin) return false;
  if (prev.onJumpTo !== next.onJumpTo) return false;
  // Reactions live inside `m`; allMessages used only to look up quoted ref by id —
  // skip reference-equality on it and rely on `m` to capture changes.
  return true;
});

function renderMedia(m) {
  const type = m.media_type;
  if (!type) return null;
  const url = m.media_url;  // outbound (admin-provided/uploaded public URL)
  const downloadUrl = url ? `${url}${url.includes("?") ? "&" : "?"}download=1` : null;
  const filename = m.filename || (url ? url.split("/").pop() : "media");
  if (type === "sticker" && url) {
    // Stickers render free-standing: transparent, small, no crop, no bubble fill.
    return (
      <div className="relative group/media" data-testid={`msg-media-sticker-${m.id}`}>
        <img src={url} alt="sticker" className="block w-[140px] h-[140px] object-contain cursor-zoom-in"
          loading="lazy"
          onClick={() => window.dispatchEvent(new CustomEvent("lightbox:open", { detail: { url, filename, downloadUrl, kind: "image" } }))} />
      </div>
    );
  }
  if (type === "image" && url) {
    return (
      <div className="relative group/media" data-testid={`msg-media-image-${m.id}`}>
        <a href={url} target="_blank" rel="noreferrer" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("lightbox:open", { detail: { url, filename, downloadUrl, kind: "image" } })); }}>
          <img src={url} alt={filename} className="block w-full max-h-[320px] object-cover bg-gray-100 cursor-zoom-in" loading="lazy" />
        </a>
        <a href={downloadUrl} download={filename} className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-black/80 text-white p-1.5 md:opacity-0 md:group-hover/media:opacity-100 transition-opacity" title={`Download ${filename}`} data-testid={`download-${m.id}`}>
          <DownloadSimple size={14} weight="bold" />
        </a>
      </div>
    );
  }
  if (type === "video" && url) {
    return (
      <div className="relative group/media" data-testid={`msg-media-video-${m.id}`}>
        <video controls preload="metadata" className="block w-full max-h-[320px] bg-black">
          <source src={url} />
        </video>
        <a href={downloadUrl} download={filename} className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-black/80 text-white p-1.5 md:opacity-0 md:group-hover/media:opacity-100 transition-opacity" title={`Download ${filename}`} data-testid={`download-${m.id}`}>
          <DownloadSimple size={14} weight="bold" />
        </a>
      </div>
    );
  }
  if (type === "document" && url) {
    return (
      <div className="flex items-center gap-2 bg-white/60 px-3 py-2 border border-gray-200 text-gray-800" data-testid={`msg-media-document-${m.id}`}>
        <span className="text-lg">📄</span>
        <a href={url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 hover:underline">
          <div className="text-xs font-semibold truncate">{filename}</div>
          <div className="text-[10px] text-gray-500 uppercase tracking-widest">Document · Click to preview</div>
        </a>
        <a href={downloadUrl} download={filename} className="text-gray-500 hover:text-[#25D366] p-1" title={`Download ${filename}`} data-testid={`download-${m.id}`}>
          <DownloadSimple size={14} weight="bold" />
        </a>
      </div>
    );
  }
  if (type === "audio" && url) {
    return (
      <div className="flex items-center gap-2" data-testid={`msg-media-audio-${m.id}`}>
        <audio controls preload="metadata" className="block flex-1 min-w-[220px]">
          <source src={url} />
        </audio>
        <a href={downloadUrl} download={filename} className="text-gray-500 hover:text-[#25D366] p-1" title={`Download ${filename}`} data-testid={`download-${m.id}`}>
          <DownloadSimple size={14} weight="bold" />
        </a>
      </div>
    );
  }
  // Inbound without a downloaded URL — show a lightweight placeholder using media_id
  if (m.media_id) {
    const icon = type === "image" ? "🖼️" : type === "video" ? "🎬" : type === "audio" ? "🎧" : type === "sticker" ? "💟" : "📄";
    return (
      <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 text-gray-700" data-testid={`msg-media-placeholder-${m.id}`}>
        <span className="text-lg">{icon}</span>
        <div className="text-[11px] uppercase tracking-widest font-bold">Incoming {type}</div>
      </div>
    );
  }
  return null;
}

function Lightbox() {
  const [state, setState] = useState(null);  // { url, filename, downloadUrl, kind }
  useEffect(() => {
    const onOpen = (e) => setState(e.detail);
    const onKey = (e) => { if (e.key === "Escape") setState(null); };
    window.addEventListener("lightbox:open", onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("lightbox:open", onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);
  if (!state) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={() => setState(null)} data-testid="lightbox">
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <a href={state.downloadUrl} download={state.filename} onClick={(e) => e.stopPropagation()}
          className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5"
          data-testid="lightbox-download">
          <DownloadSimple size={14} weight="bold" /> Download
        </a>
        <button onClick={() => setState(null)} className="bg-white/10 hover:bg-white/20 text-white p-2" data-testid="lightbox-close">
          <X size={18} weight="bold" />
        </button>
      </div>
      <ZoomableImage key={state.url} src={state.url} alt={state.filename} onClose={() => setState(null)} />
      <div className="absolute bottom-3 inset-x-0 text-center text-white text-xs opacity-70 pointer-events-none px-4 truncate">
        {state.filename} · pinch or double-tap to zoom
      </div>
    </div>
  );
}

/* Photo viewer: pinch / double-tap / mouse-wheel zoom, drag to move when zoomed. */
function ZoomableImage({ src, alt, onClose }) {
  const [view, setView] = useState({ s: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  const g = useRef({ pts: new Map(), base: null, moved: false, lastTap: 0, tapTimer: null });
  const boxRef = useRef(null);

  const apply = (v) => {
    const s = Math.min(8, Math.max(1, v.s));
    const nv = s <= 1.01 ? { s: 1, x: 0, y: 0 } : { s, x: v.x, y: v.y };
    viewRef.current = nv;
    setView(nv);
  };
  const center = () => {
    const r = boxRef.current?.getBoundingClientRect();
    return r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : { x: 0, y: 0 };
  };
  // zoom to scale s keeping the point p (screen coords) where it is
  const zoomAt = (p, s, from = viewRef.current) => {
    const c = center();
    const px = p.x - c.x, py = p.y - c.y;
    const k = Math.min(8, Math.max(1, s)) / from.s;
    apply({ s, x: px - (px - from.x) * k, y: py - (py - from.y) * k });
  };
  const begin = () => {
    g.current.base = { v: viewRef.current, pts: [...g.current.pts.values()].map((p) => ({ ...p })) };
  };
  const down = (e) => {
    e.stopPropagation();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    g.current.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (g.current.pts.size === 1) { g.current.moved = false; g.current.onImg = e.target?.tagName === "IMG"; }
    begin();
  };
  const move = (e) => {
    if (!g.current.pts.has(e.pointerId)) return;
    g.current.pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const b = g.current.base;
    if (!b) return;
    const pts = [...g.current.pts.values()];
    if (pts.length >= 2 && b.pts.length >= 2) {
      const d0 = Math.hypot(b.pts[0].x - b.pts[1].x, b.pts[0].y - b.pts[1].y) || 1;
      const d1 = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const c = center();
      const c0 = { x: (b.pts[0].x + b.pts[1].x) / 2 - c.x, y: (b.pts[0].y + b.pts[1].y) / 2 - c.y };
      const c1 = { x: (pts[0].x + pts[1].x) / 2 - c.x, y: (pts[0].y + pts[1].y) / 2 - c.y };
      const s = Math.min(8, Math.max(1, b.v.s * d1 / d0));
      const k = s / b.v.s;
      apply({ s, x: c1.x - (c0.x - b.v.x) * k, y: c1.y - (c0.y - b.v.y) * k });
      g.current.moved = true;
    } else if (pts.length === 1 && b.pts.length >= 1) {
      const dx = pts[0].x - b.pts[0].x, dy = pts[0].y - b.pts[0].y;
      if (Math.abs(dx) + Math.abs(dy) > 6) g.current.moved = true;
      if (b.v.s > 1) apply({ ...b.v, x: b.v.x + dx, y: b.v.y + dy });
    }
  };
  const up = (e) => {
    g.current.pts.delete(e.pointerId);
    begin();                     // a finger still down carries on as a drag from here
    if (g.current.pts.size || g.current.moved) return;
    const now = Date.now();
    if (now - g.current.lastTap < 300) {              // double tap
      clearTimeout(g.current.tapTimer);
      g.current.lastTap = 0;
      if (viewRef.current.s > 1) apply({ s: 1, x: 0, y: 0 });
      else zoomAt({ x: e.clientX, y: e.clientY }, 3, { s: 1, x: 0, y: 0 });
      return;
    }
    g.current.lastTap = now;
    // single tap on the dark background (not zoomed) closes the viewer
    if (!g.current.onImg && viewRef.current.s === 1) {
      g.current.tapTimer = setTimeout(() => onClose?.(), 300);
    }
  };
  const wheel = (e) => {
    zoomAt({ x: e.clientX, y: e.clientY }, viewRef.current.s * Math.exp(-e.deltaY * 0.0015));
  };
  useEffect(() => () => clearTimeout(g.current.tapTimer), []);

  return (
    <div ref={boxRef} className="absolute inset-0 flex items-center justify-center overflow-hidden select-none"
      style={{ touchAction: "none" }}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onWheel={wheel}
      data-testid="lightbox-zoom">
      <img src={src} alt={alt} draggable={false}
        className="max-w-[94vw] max-h-[86vh] object-contain"
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})`, transformOrigin: "center center",
                 willChange: "transform", cursor: view.s > 1 ? "grab" : "zoom-in" }} />
    </div>
  );
}

function renderLocation(m) {
  const loc = m.location || {};
  const lat = loc.latitude, lng = loc.longitude;
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  const mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
  const openHref = `https://maps.google.com/maps?q=${lat},${lng}`;
  return (
    <div className="-mx-1.5 -my-1.5 mb-0.5 overflow-hidden" data-testid={`msg-location-${m.id}`}>
      <iframe title={`map-${m.id}`} src={mapSrc} width="100%" height="160" loading="lazy" className="block border-0" />
      <a href={openHref} target="_blank" rel="noreferrer" className="block px-3 py-2 bg-white text-xs border-t border-gray-200 hover:bg-gray-50">
        <div className="flex items-center gap-1.5">
          <span className="text-[#0891B2]">📍</span>
          <div className="min-w-0 flex-1">
            {loc.name && <div className="font-semibold truncate">{loc.name}</div>}
            {loc.address && <div className="text-gray-500 truncate">{loc.address}</div>}
            {!loc.name && !loc.address && <div className="font-mono text-[10px] text-gray-500">{lat.toFixed(4)}, {lng.toFixed(4)}</div>}
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[#002FA7] font-bold">Open →</span>
        </div>
      </a>
    </div>
  );
}

function renderContacts(m) {
  const contacts = m.contacts || [];
  if (!contacts.length) return null;
  return (
    <div className="space-y-1.5" data-testid={`msg-contacts-${m.id}`}>
      {contacts.map((c, i) => {
        const name = (c.name || {}).formatted_name || (c.name || {}).first_name || "Contact";
        const firstPhone = (c.phones || [])[0]?.phone;
        const firstEmail = (c.emails || [])[0]?.email;
        const org = (c.org || {}).company;
        return (
          <div key={i} className="bg-white/70 border border-gray-200 px-3 py-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center text-white font-bold">
                {(name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{name}</div>
                {org && <div className="text-[10px] text-gray-500 truncate">{org}</div>}
                {firstPhone && <a href={`tel:${firstPhone}`} className="text-[11px] text-[#002FA7] hover:underline block truncate">{firstPhone}</a>}
                {firstEmail && <a href={`mailto:${firstEmail}`} className="text-[11px] text-[#002FA7] hover:underline block truncate">{firstEmail}</a>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------- New chat modal ----------------
function NewChatModal({ onClose, onCreated, execs, isAdmin }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [requirement, setRequirement] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [requesting, setRequesting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setConflict(null);
    try {
      const payload = { phone, customer_name: name, requirement };
      if (isAdmin && assignedTo) payload.assigned_to = assignedTo;
      const { data } = await api.post("/inbox/start-chat", payload);
      toast.success("Chat ready");
      onCreated(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "duplicate_phone") {
        setConflict(detail);
      } else {
        toast.error(errMsg(e));
      }
    }
    finally { setBusy(false); }
  };

  const requestReassignment = async () => {
    if (!conflict?.existing_lead_id) return;
    setRequesting(true);
    try {
      await api.post("/inbox/transfer-request", {
        lead_id: conflict.existing_lead_id,
        reason: `Tried to start chat with ${phone}. Please reassign to me.`,
      });
      toast.success("Reassignment request sent to admin");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRequesting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="new-chat-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md bg-white border border-gray-900 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">New Chat</div>
            <h2 className="font-chivo font-black text-2xl mt-1">Start by phone</h2>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400"><X size={18} /></button>
        </div>
        {conflict && (
          <div className="border-l-4 border-[#E60000] bg-[#FFE9E9] px-3 py-2 mb-3" data-testid="chat-duplicate-conflict-panel">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#E60000]">Duplicate phone</div>
            <div className="text-sm mt-1">{conflict.message}</div>
            <div className="text-xs text-gray-700 mt-1">Currently with: <b>{conflict.owned_by_name || "another executive"}</b></div>
            <div className="flex gap-2 mt-2">
              <button type="button" onClick={requestReassignment} disabled={requesting} className="bg-[#E60000] hover:bg-[#cc0000] text-white px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50" data-testid="chat-request-reassignment-btn">
                {requesting ? "Sending…" : "Request Reassignment"}
              </button>
              <button type="button" onClick={() => setConflict(null)} className="border border-gray-300 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-100">Edit phone</button>
            </div>
          </div>
        )}
        <div className="space-y-3">
          <Field label="Phone *">
            <input required autoFocus value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="8790934618 or +255123456789"
              className="w-full border border-gray-300 px-3 py-2 text-sm font-mono" data-testid="new-chat-phone-input" />
          </Field>
          <Field label="Customer name">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional"
              className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="new-chat-name-input" />
          </Field>
          <Field label="Requirement / context">
            <input value={requirement} onChange={(e) => setRequirement(e.target.value)} placeholder="What are they enquiring about?"
              className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="new-chat-requirement-input" />
          </Field>
          {isAdmin && (
            <Field label="Assign to">
              <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full border border-gray-300 px-2 py-2 text-sm" data-testid="new-chat-assign-select">
                <option value="">Auto (round-robin)</option>
                {execs.map(x => <option key={x.id} value={x.id}>{x.role === "admin" ? `${x.name} (admin)` : x.name}</option>)}
              </select>
            </Field>
          )}
          <div className="text-xs text-gray-500 leading-relaxed border border-gray-200 bg-gray-50 p-3">
            If a lead with this phone already exists and is yours, it'll open. If it's another executive's lead,
            you can send a reassignment request to admin. WhatsApp Cloud API requires a template message for
            first-touch outside the 24-hour window.
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 text-[10px] uppercase tracking-widest font-bold hover:bg-gray-100" data-testid="new-chat-cancel-btn">Cancel</button>
          <button disabled={busy} className="bg-[#25D366] hover:bg-[#1da851] text-white px-4 py-2 text-[10px] uppercase tracking-widest font-bold disabled:opacity-50" data-testid="new-chat-submit-btn">
            {busy ? "Starting…" : "Start Chat"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">{label}</div>
      {children}
    </label>
  );
}


// ---------------- Internal Admin ↔ Agent Q&A ----------------
function InternalChat({ leadId, currentUser, assignedTo, execs, quote, clearQuote, preselectAgentId, onPreselectConsumed, onJumpToWa }) {
  const isAdmin = currentUser?.role === "admin";
  const [threads, setThreads] = useState([]); // admin view
  const [activeAgentId, setActiveAgentId] = useState(preselectAgentId || null); // admin: selected thread
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Consume the preselect on mount (admin deep-link from /qa)
  useEffect(() => {
    if (preselectAgentId && !activeAgentId) {
      setActiveAgentId(preselectAgentId);
    }
    if (preselectAgentId) onPreselectConsumed?.();
    // eslint-disable-next-line
  }, [preselectAgentId]);

  const canPost = isAdmin ? !!activeAgentId : (assignedTo === currentUser?.id);

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      if (isAdmin && !activeAgentId) {
        const { data } = await api.get(`/internal-chat/${leadId}`);
        setThreads(data.threads || []);
        return;
      }
      const params = isAdmin ? { agent_id: activeAgentId } : {};
      const { data } = await api.get(`/internal-chat/${leadId}`, { params });
      setMsgs(data.thread || []);
      try { await api.post(`/internal-chat/${leadId}/mark-read`, null, { params: isAdmin ? { agent_id: activeAgentId } : {} }); } catch { /* ignore */ }
    } catch (e) {
      const msg = errMsg(e, "");
      if (msg && !msg.toLowerCase().includes("network")) toast.error(msg);
    }
  }, [leadId, isAdmin, activeAgentId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs.length]);

  const send = async () => {
    if (!draft.trim() || !canPost) return;
    setSending(true);
    try {
      const payload = { lead_id: leadId, body: draft };
      if (isAdmin) payload.to_user_id = activeAgentId;
      if (quote?.id) payload.message_id = quote.id;
      await api.post("/internal-chat/send", payload);
      setDraft("");
      clearQuote?.();
      await load();
    } catch (e) { toast.error(errMsg(e, "Failed to send")); }
    finally { setSending(false); }
  };

  if (isAdmin && !activeAgentId) {
    return (
      <div className="flex-1 overflow-y-auto p-4" data-testid="internal-admin-thread-list">
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-3">Private Q&amp;A threads</div>
        {threads.length === 0 && (
          <div className="text-xs text-gray-400 italic">
            No agents have started a private conversation on this lead yet.
            Agents can ask a question by clicking the <Question size={10} className="inline" weight="bold" /> icon on any WhatsApp message.
          </div>
        )}
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Start thread with</div>
          <select
            onChange={(e) => e.target.value && setActiveAgentId(e.target.value)}
            value=""
            className="w-full border border-gray-300 px-2 py-2 text-sm"
            data-testid="internal-admin-start-with"
          >
            <option value="">— Select agent —</option>
            {execs.filter((x) => x.role === "executive").map((x) => (
              <option key={x.id} value={x.id}>{x.name} (@{x.username})</option>
            ))}
          </select>
        </div>
        <div className="mt-4 space-y-2">
          {threads.map((t) => (
            <button
              key={t.agent_id}
              onClick={() => setActiveAgentId(t.agent_id)}
              className="w-full text-left border border-gray-200 hover:border-[#002FA7] hover:bg-[#002FA7]/5 p-3"
              data-testid={`internal-thread-${t.agent_username}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{t.agent_name || t.agent_username || t.agent_id}</div>
                {t.unread_for_admin > 0 && (
                  <span className="bg-[#E60000] text-white text-[10px] px-1.5 py-0.5 font-bold rounded-full">{t.unread_for_admin}</span>
                )}
              </div>
              <div className="text-xs text-gray-600 truncate mt-1">{t.last_body || "—"}</div>
              <div className="text-[10px] text-gray-400 font-mono mt-1">{t.last_at ? fmtIST(t.last_at) : ""} · {t.count} msg{t.count === 1 ? "" : "s"}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const headerName = isAdmin
    ? (execs.find((x) => x.id === activeAgentId)?.name || "Agent")
    : "Admin";

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="internal-chat-thread">
      <div className="px-4 py-2 border-b border-gray-200 shrink-0 flex items-center gap-2">
        {isAdmin && (
          <button onClick={() => { setActiveAgentId(null); clearQuote?.(); }} className="text-gray-500 hover:text-gray-900" title="Back to threads" data-testid="internal-back-btn">
            <CaretLeft size={14} weight="bold" />
          </button>
        )}
        <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
          Private with {headerName} · invisible to customer
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#F7F7F5]" data-testid="internal-messages-area">
        {msgs.length === 0 && (
          <div className="text-center text-xs text-gray-400 italic py-6">No messages yet — start the conversation.</div>
        )}
        {msgs.map((m) => {
          const mine = m.from_user_id === currentUser?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`internal-msg-${m.id}`}>
              <div className={`max-w-[85%] px-3 py-2 text-sm shadow-sm ${mine ? "bg-[#D6E4FF] border border-[#002FA7]/20" : "bg-white border border-gray-200"}`}>
                {m.quoted && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (m.quoted?.id && onJumpToWa) onJumpToWa(m.quoted.id);
                    }}
                    className="block w-full text-left mb-1 border-l-[3px] pl-2 py-1 text-[11px] bg-black/5 hover:bg-black/10 active:bg-black/15 transition-colors cursor-pointer"
                    style={{ borderColor: m.quoted.direction === "out" ? "#25D366" : "#002FA7" }}
                    title="Jump to original WA message"
                    data-testid={`internal-quoted-${m.id}`}
                  >
                    <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: m.quoted.direction === "out" ? "#128C7E" : "#002FA7" }}>
                      {m.quoted.direction === "out" ? "You (WA)" : "Customer"}
                    </div>
                    <div className="text-gray-700 truncate">{m.quoted.body || "(media)"}</div>
                  </button>
                )}
                <div className="whitespace-pre-wrap break-words">{m.body}</div>
                <div className="text-[10px] text-gray-500 font-mono mt-1 flex items-center justify-between gap-2">
                  <span>{m.from_role === "admin" ? "Admin" : "Agent"}</span>
                  <span title={fmtSmartLong(m.at)}>{fmtTime12(m.at)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {canPost ? (
        <div className="border-t border-gray-200 bg-white shrink-0">
          {quote && (
            <div className="px-3 pt-2 flex items-start gap-2" data-testid="internal-quote-preview">
              <div className="flex-1 min-w-0 border-l-[3px] pl-2 py-1 text-xs bg-black/5" style={{ borderColor: quote.direction === "out" ? "#25D366" : "#002FA7" }}>
                <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: quote.direction === "out" ? "#128C7E" : "#002FA7" }}>
                  Asking about {quote.direction === "out" ? "your message" : "customer message"}
                </div>
                <div className="text-gray-700 truncate">{quote.preview || "(media)"}</div>
              </div>
              <button onClick={clearQuote} className="text-gray-500 hover:text-gray-900 p-1" data-testid="internal-quote-cancel">
                <X size={12} />
              </button>
            </div>
          )}
          <div className="p-2 flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={isAdmin ? "Reply privately to agent…" : "Ask admin privately…"}
              rows={1}
              className="flex-1 resize-none border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#002FA7]"
              data-testid="internal-chat-input"
            />
            <button
              onClick={send}
              disabled={!draft.trim() || sending}
              className="bg-[#002FA7] hover:bg-[#002288] text-white p-2 disabled:opacity-50"
              data-testid="internal-send-btn"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          </div>
        </div>
      ) : (
        <div className="border-t border-[#E60000] bg-[#FFE9E9] text-[#E60000] text-xs p-3 text-center shrink-0">
          You can only use internal Q&amp;A on leads assigned to you.
        </div>
      )}
    </div>
  );
}
